import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  TrendingUp,
  CheckCircle,
  Warning,
  Schedule,
  Payment,
  Autorenew,
  AssignmentTurnedIn,
  Receipt,
  PendingActions,
  Download,
  Refresh,
  Visibility,
  Person,
  AccessTime,
  Dashboard,
  BarChart,
  PieChart,
  WarningAmber,
  InfoOutlined,
  ErrorOutline,
  Brightness4,
  Brightness7,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
  ComposedChart,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const GILROY_MEDIUM = "GilroyMedium";
const GILROY_BOLD = "GilroyBold";
const PDF_EXPORT_WIDTH = 1280;
const PDF_EXPORT_MIN_HEIGHT = 900;

/* ─────────────────────────── Helpers ─────────────────────────── */
const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/[,\u20B9\s]/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const toINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);

const getRecordStatusDisplay = (status) => {
  const s = String(status || "").trim().toUpperCase();
  if (s === "APPROVED") return "Approved";
  if (s === "PENDING") return "Awaiting";
  if (s === "IN_REVIEW") return "In Review";
  if (s === "REJECTED") return "Rejected";
  return status || "-";
};

const getRecordStatusBadgeStyle = (status) => {
  const s = String(status || "").trim().toUpperCase();
  if (s === "APPROVED") return { color: "#fff", backgroundColor: "#47a34b" };
  if (s === "IN_REVIEW") return { color: "#ed6c02", backgroundColor: "#fff3e0" };
  if (s === "REJECTED") return { color: "#fff", backgroundColor: "#ee4646" };
  return { color: "#fff", backgroundColor: "#ec8735" };
};

/* ─────────────────────────── Pickers / Reconcile ─────────────────────────── */
function aggregateByPart(items, pick) {
  const m = new Map();
  for (const it of items) {
    const part = it.part_no || it.item_code || it.code || (it.part && it.part.code) || "";
    if (!part) continue;
    const key = String(part).trim();
    m.set(key, (m.get(key) || 0) + pick(it));
  }
  return m;
}

function poPickers(poItems) {
  return {
    qtyByPart: aggregateByPart(poItems, (it) =>
      toNum(it["qty."] ?? it.qty ?? it.quantity ?? it.parsed_qty ?? it["parsed_qty."])
    ),
    priceByPart: aggregateByPart(poItems, (it) =>
      toNum(it.parsed_rate ?? it.rate ?? it.unit_price)
    ),
    hsnByPart: (() => {
      const m = new Map();
      for (const it of poItems) {
        const part = it.part_no?.toString().trim();
        if (!part) continue;
        if (!m.has(part)) m.set(part, (it.hsn_code ?? it.hsn ?? "").toString().trim());
      }
      return m;
    })(),
  };
}

function grnPickers(grnItems) {
  return {
    qtyByPart: aggregateByPart(grnItems, (it) =>
      toNum(
        it.quantity_received ?? it.received_quantity ?? it.parsed_received_quantity ??
        it.received_q ?? it.qty ?? it.parsed_qty
      )
    ),
  };
}

function invPickers(invItems) {
  return {
    qtyByPart: aggregateByPart(invItems, (it) =>
      toNum(it.qty ?? it.quantity ?? it.received_quantity ?? it.parsed_qty ?? it.parsed_quantity)
    ),
    priceByPart: aggregateByPart(invItems, (it) =>
      toNum(it.unit_price ?? it.rate ?? it.parsed_unit_price ?? it.parsed_rate)
    ),
    hsnByPart: (() => {
      const m = new Map();
      for (const it of invItems) {
        const part = it.part_no || it.code || (it.part && it.part.code) || "";
        if (!part) continue;
        if (!m.has(part)) m.set(part, (it.hsn ?? it.hsn_code ?? "").toString().trim());
      }
      return m;
    })(),
  };
}

function reconcileRecord(rec) {
  const rj = rec.result_json || rec.extracted_data || {};
  const po = rj.po_data?.[0] ?? {};
  const grn = rj.grn_data?.[0] ?? {};
  const inv = rj.invoice_data?.[0] ?? {};
  const createdAt = rec.created_at || rec.created_on || rec.created_date || rec.createdAt || null;

  const poItems = po.line_items?.map((x) => ({
    part_no: x.part_no, qty: x.qty ?? x["qty."], "qty.": x["qty."],
    parsed_qty: x.parsed_qty ?? x["parsed_qty."], rate: x.rate,
    parsed_rate: x.parsed_rate, hsn_code: x.hsn_code,
  })) ?? [];
  const grnItems = grn.line_items?.map((x) => ({
    part_no: x.part_no ?? x.item_code,
    received_q: x.quantity_received ?? x.received_quantity ?? x.received_q,
    quantity_received: x.quantity_received,
    received_quantity: x.received_quantity ?? x.quantity_received,
    parsed_qty: x.parsed_received_quantity ?? x.parsed_qty,
  })) ?? [];
  const invItems = inv.line_items?.map((x) => ({
    part_no: x.part_no ?? x.code, qty: x.qty ?? x.quantity,
    parsed_qty: x.parsed_qty ?? x.parsed_quantity, unit_price: x.unit_price,
    parsed_unit_price: x.parsed_unit_price, hsn: x.hsn ?? x.hsn_code,
  })) ?? [];

  const { qtyByPart: poQty, priceByPart: poPrice, hsnByPart: poHSN } = poPickers(poItems);
  const { qtyByPart: grnQty } = grnPickers(grnItems);
  const { qtyByPart: invQty, priceByPart: invPrice, hsnByPart: invHSN } = invPickers(invItems);

  const poParts = new Set([...poQty.keys()]);
  const grnParts = new Set([...grnQty.keys()]);
  const invParts = new Set([...invQty.keys()]);

  const mismatches = [];
  const inc = (r) => mismatches.push(r);

  for (const p of poParts) {
    if (!grnParts.has(p)) inc(`Missing in GRN: ${p}`);
    if (!invParts.has(p)) inc(`Missing in Invoice: ${p}`);
  }
  for (const p of grnParts) if (!poParts.has(p)) inc(`Extra in GRN: ${p}`);
  for (const p of invParts) if (!poParts.has(p)) inc(`Extra in Invoice: ${p}`);

  for (const p of poParts) {
    const qPO = poQty.get(p) ?? 0, qGRN = grnQty.get(p) ?? 0, qINV = invQty.get(p) ?? 0;
    if (qPO !== qGRN || qPO !== qINV) inc(`Quantity mismatch: ${p} (PO ${qPO} vs GRN ${qGRN} vs INV ${qINV})`);
    const prPO = poPrice.get(p), prINV = invPrice.get(p);
    if (Number.isFinite(prPO) && Number.isFinite(prINV) && prPO !== prINV)
      inc(`Price mismatch: ${p} (PO ${toINR(prPO)} vs INV ${toINR(prINV)})`);
    const hPO = poHSN.get(p), hINV = invHSN.get(p);
    if (hPO && hINV && hPO !== hINV) inc(`HSN mismatch: ${p} (PO ${hPO} vs INV ${hINV})`);
  }

  const invAmount = toNum(inv.invoice_amount ?? inv.total_amount ?? inv.amount);
  const matchTimeMinutes = (() => {
    const invDateRaw = inv.invoice_date || inv.date || inv.doc_date;
    if (!invDateRaw || !createdAt) return null;
    const diff = (new Date(createdAt) - new Date(invDateRaw)) / (1000 * 60);
    return Number.isFinite(diff) && diff >= 0 ? Math.round(diff) : null;
  })();

  const hasQtyOrPriceOrItemIssue = mismatches.some((m) =>
    /Quantity mismatch|Price mismatch|Missing|Extra/.test(m)
  );
  let classification = "Fully Matched";
  if (hasQtyOrPriceOrItemIssue) classification = "With Exceptions";
  else if (mismatches.length > 0 || rec.status === "IN_REVIEW" || rec.status === "PENDING")
    classification = "Partially Matched";

  const readyForPayment = rec.status === "APPROVED" && classification !== "With Exceptions";
  const reasonBuckets = mismatches.map((m) => {
    if (m.startsWith("Quantity mismatch")) return "Quantity mismatch";
    if (m.startsWith("Price mismatch")) return "Price mismatch";
    if (m.startsWith("HSN mismatch")) return "Tax/HSN mismatch";
    if (m.startsWith("Missing")) return "Item Missing";
    if (m.startsWith("Extra")) return "Extra item";
    return "Meta mismatch";
  });

  return {
    classification, readyForPayment, invAmount, mismatches, reasonBuckets,
    createdAt, matchTimeMinutes, hasException: classification === "With Exceptions",
    matchCode: rec.match_code, status: rec.status,
  };
}

function computeKPIs(data) {
  const per = data.map(reconcileRecord);
  const total = per.length;
  const approved = per.filter((x) => x.status === "APPROVED").length;
  const fully = per.filter((x) => x.classification === "Fully Matched").length;
  const partial = per.filter((x) => x.classification === "Partially Matched").length;
  const exceptions = per.filter((x) => x.classification === "With Exceptions");
  const withExceptions = exceptions.length;
  const readyPay = per.filter((x) => x.readyForPayment).length;
  const totalExceptionValue = exceptions.reduce((s, x) => s + (x.invAmount || 0), 0);
  const now = new Date();
  const agingCount = exceptions.filter((x) => {
    if (!x.createdAt) return false;
    return (now - new Date(x.createdAt)) / (1000 * 60 * 60 * 24) > 7;
  }).length;
  const reasonCount = new Map();
  per.forEach((p) => p.reasonBuckets.forEach((r) => reasonCount.set(r, (reasonCount.get(r) || 0) + 1)));
  const top3 = [...reasonCount.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  const automated = data.filter((d) => !!(d.result_json || d.extracted_data)).length;
  const automationCoverage = total ? Math.round((automated / total) * 100) : 0;
  const delayed = per.filter((x) => x.hasException && !x.readyForPayment);
  const delayedCount = delayed.length;
  const delayedValue = delayed.reduce((s, x) => s + (x.invAmount || 0), 0);
  const manualEffortSaved = (automated / 100).toFixed(1);
  const matchTimes = per.map((p) => p.matchTimeMinutes).filter((v) => Number.isFinite(v));
  const avgMatchingTime = matchTimes.length
    ? Math.round(matchTimes.reduce((s, v) => s + v, 0) / matchTimes.length) : null;

  return {
    totals: {
      approved, total, fully, partial, withExceptions, readyPay,
      avgMatchingTime, automationCoverage, totalExceptionValue,
      agingCount, delayedCount, delayedValue, manualEffortSaved,
    },
    top3, per,
  };
}

function computeSummaryKPIs(summaryData) {
  const metrics = summaryData?.dashboard_metrics || {};
  const analytics = summaryData?.analytics_kpis || {};
  const total = Number(metrics.total_matching_done ?? summaryData?.total_count ?? 0);
  const fully = Number(metrics.fully_matched_invoices ?? 0);
  const partial = Number(metrics.partially_matched_invoices ?? 0);
  const withExceptions = Number(metrics.invoices_with_exceptions ?? 0);
  const incomplete = Number(metrics.incomplete_document_invoices ?? 0);
  const notMatched = Number(metrics.not_matched_invoices ?? 0);
  const readyPay = fully;
  const matchedPercent = Number(
    analytics.matched_or_partially_matched_percent ??
      (total ? ((fully + partial) / total) * 100 : 0)
  );
  const exceptionPercent = Number(
    analytics.invoice_exception_percent ??
      (total ? (withExceptions / total) * 100 : 0)
  );
  const monthwise = metrics.monthwise_matching_count || {};
  const per = Object.entries(monthwise).map(([yearMonth, count]) => {
    const [year, month] = String(yearMonth).split("-");
    const date = `${year}-${month || "01"}-01T00:00:00`;
    const invoices = Number(count || 0);
    return {
      createdAt: date,
      classification: "Summary",
      invoices,
      matched: Math.round((invoices * matchedPercent) / 100),
      reasonBuckets: [],
    };
  });

  return {
    totals: {
      approved: readyPay,
      total,
      fully,
      partial,
      withExceptions,
      readyPay,
      avgMatchingTime: null,
      automationCoverage: Math.round(matchedPercent || 0),
      totalExceptionValue: 0,
      agingCount: incomplete,
      delayedCount: notMatched,
      delayedValue: 0,
      manualEffortSaved: Number(analytics.average_invoices_per_matching ?? 0).toFixed(1),
      exceptionPercent,
    },
    top3: (metrics.top_3_mismatch_reasons || analytics.top_mismatch_reasons || [])
      .map((item) => [item?.reason || "Unknown", Number(item?.count || 0)]),
    per,
  };
}

const SUMMARY_COLORS = {
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  soft: "#f8fafc",
  blue: "#2563eb",
  cyan: "#0891b2",
  green: "#16a34a",
  amber: "#d97706",
  red: "#dc2626",
  purple: "#7c3aed",
  slate: "#475569",
};

const summaryFont = {
  fontFamily: GILROY_MEDIUM,
  fontSize: 14,
};

const summaryTick = {
  fill: SUMMARY_COLORS.muted,
  fontSize: 14,
  fontFamily: GILROY_MEDIUM,
};

const summarySeries = [
  SUMMARY_COLORS.blue,
  SUMMARY_COLORS.green,
  SUMMARY_COLORS.amber,
  SUMMARY_COLORS.red,
  SUMMARY_COLORS.purple,
  SUMMARY_COLORS.cyan,
];

const asSummaryArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  if (Array.isArray(value.data)) return value.data;
  if (Array.isArray(value.results)) return value.results;
  if (Array.isArray(value.items)) return value.items;
  if (Array.isArray(value.rows)) return value.rows;
  return [];
};

const asSummaryObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const getNestedSummaryValue = (source, path) => {
  if (!source || !path) return undefined;
  return String(path)
    .split(".")
    .reduce((current, key) => current?.[key], source);
};

const getFirstSummaryValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return 0;
};

const getSummaryMetricValue = (sources, paths) => {
  const sourceList = Array.isArray(sources) ? sources : [sources];
  for (const source of sourceList) {
    for (const path of paths) {
      const value = getNestedSummaryValue(source, path);
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
  }
  return 0;
};

const getSummaryPeriodRows = (value, mode = "monthly") => {
  if (Array.isArray(value)) return value;
  const objectValue = asSummaryObject(value);
  return asSummaryArray(
    objectValue[mode] ||
      objectValue[mode?.toLowerCase?.()] ||
      objectValue.monthly ||
      objectValue.weekly ||
      objectValue.yearly
  );
};

const TOP_SUMMARY_RECORD_LIMIT = 10;

const getSummaryYearLabel = (row = {}) => {
  const raw = row.year ?? row.period ?? row.label ?? "";
  const year = String(raw).match(/\d{4}/)?.[0];
  return year || "Unknown";
};

const buildSummaryYearlyTrend = (summaryData = {}) => {
  const invoiceTrend =
    summaryData?.invoice_analytics?.invoice_value_trend ||
    summaryData?.invoice_value_trend;
  const trendAnalytics = summaryData?.trend_analytics || {};
  const yearlyRows =
    getSummaryPeriodRows(invoiceTrend, "yearly").length
      ? getSummaryPeriodRows(invoiceTrend, "yearly")
      : asSummaryArray(
          summaryData?.yearwise_invoice_value ||
            summaryData?.invoice_analytics?.yearwise_invoice_value,
        );

  if (yearlyRows.length) {
    return yearlyRows
      .map((row) => ({
        year: getSummaryYearLabel(row),
        invoiceValue: toNum(row.invoice_value ?? row.value ?? row.amount),
        invoiceCount: toNum(row.invoice_count ?? row.count ?? row.total_invoices),
      }))
      .filter((row) => row.year !== "Unknown" || row.invoiceValue || row.invoiceCount);
  }

  const monthlyRows = getSummaryPeriodRows(invoiceTrend, "monthly");
  if (monthlyRows.length) {
    const byYear = monthlyRows.reduce((acc, row) => {
      const year = getSummaryYearLabel(row);
      if (!acc[year]) acc[year] = { year, invoiceValue: 0, invoiceCount: 0 };
      acc[year].invoiceValue += toNum(row.invoice_value ?? row.value ?? row.amount);
      acc[year].invoiceCount += toNum(row.invoice_count ?? row.count ?? row.total_invoices);
      return acc;
    }, {});

    return Object.values(byYear).sort((a, b) => String(a.year).localeCompare(String(b.year)));
  }

  return asSummaryArray(
    trendAnalytics.previous_two_years ||
      trendAnalytics.yearwise_invoice_value ||
      summaryData?.previous_two_years,
  )
    .map((row) => ({
      year: getSummaryYearLabel(row),
      invoiceValue: toNum(row.invoice_value ?? row.value ?? row.amount),
      invoiceCount: toNum(row.invoice_count ?? row.count ?? row.total_invoices),
    }))
    .filter((row) => row.year !== "Unknown" || row.invoiceValue || row.invoiceCount);
};

const getInvoiceTrendSource = (summaryData = {}) =>
  summaryData?.invoice_analytics?.invoice_value_trend ||
  summaryData?.invoice_value_trend ||
  {};

const normalizeInvoiceTrendRow = (row = {}) => ({
  year: getSummaryYearLabel(row),
  invoiceValue: toNum(
    row.invoice_value ??
      row.total_invoice_value ??
      row.invoice_total_amount ??
      row.value ??
      row.amount,
  ),
  invoiceCount: toNum(
    row.invoice_count ??
      row.total_invoices ??
      row.count ??
      row.record_count,
  ),
});

const buildYearWiseInvoiceValueRows = (...summarySources) => {
  for (const summaryData of summarySources) {
    const trendSource = getInvoiceTrendSource(summaryData);
    const yearlyRows = getSummaryPeriodRows(trendSource, "yearly")
      .map(normalizeInvoiceTrendRow)
      .filter((row) => row.year !== "Unknown" || row.invoiceValue || row.invoiceCount);

    if (yearlyRows.length) return yearlyRows;

    const aggregateRows = [
      ...getSummaryPeriodRows(trendSource, "monthly"),
      ...getSummaryPeriodRows(trendSource, "weekly"),
    ];
    if (aggregateRows.length) {
      const byYear = aggregateRows.reduce((acc, row) => {
        const normalizedRow = normalizeInvoiceTrendRow(row);
        if (!acc[normalizedRow.year]) {
          acc[normalizedRow.year] = {
            year: normalizedRow.year,
            invoiceValue: 0,
            invoiceCount: 0,
          };
        }
        acc[normalizedRow.year].invoiceValue += normalizedRow.invoiceValue;
        acc[normalizedRow.year].invoiceCount += normalizedRow.invoiceCount;
        return acc;
      }, {});

      const rows = Object.values(byYear)
        .filter((row) => row.year !== "Unknown" || row.invoiceValue || row.invoiceCount)
        .sort((a, b) => String(a.year).localeCompare(String(b.year)));

      if (rows.length) return rows;
    }
  }

  return [];
};

const formatSummaryNumber = (value, options = {}) =>
  toNum(value).toLocaleString("en-IN", options);

const formatSummaryCurrency = (value) => toINR(toNum(value));

const formatSummaryCompactCurrency = (value) => {
  const number = toNum(value);
  if (Math.abs(number) >= 10000000) return `\u20B9 ${(number / 10000000).toFixed(1)} Cr`;
  if (Math.abs(number) >= 100000) return `\u20B9 ${(number / 100000).toFixed(1)} L`;
  return formatSummaryCurrency(number);
};

const formatSummaryPercent = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  return `${formatSummaryNumber(value, { maximumFractionDigits: 2 })}%`;
};

const formatSummaryPeriod = (period) => {
  if (String(period || "").includes("-W")) return period || "-";
  const [year, month] = String(period || "").split("-");
  if (!year || !month) return period || "-";
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "2-digit",
  });
};

const truncateSummaryLabel = (value, max = 28) => {
  const text = String(value || "Unknown").trim();
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
};

const formatSummaryFieldLabel = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatSummaryRecordValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);

  const normalizedKey = String(key || "").toLowerCase();
  if (
    normalizedKey.includes("value") ||
    normalizedKey.includes("amount") ||
    normalizedKey.includes("purchase") ||
    normalizedKey.includes("spend")
  ) {
    return formatSummaryCompactCurrency(value);
  }

  if (typeof value === "number") return formatSummaryNumber(value);
  return String(value);
};

const buildSummaryChartRows = (rows, labelKeys, valueKeys) =>
  asSummaryArray(rows)
    .map((row, index) => {
      const labelKey = labelKeys.find((key) => row?.[key] !== undefined && row?.[key] !== null);
      const valueKey = valueKeys.find((key) => Number.isFinite(Number(row?.[key])));
      return {
        label: labelKey ? row[labelKey] : `Record ${index + 1}`,
        value: valueKey ? toNum(row[valueKey]) : 0,
      };
    })
    .filter((row) => row.label && Number.isFinite(row.value));

const getMatchingModeResults = (summaryData, matchingMode) => {
  const results = asSummaryArray(summaryData?.results);
  const normalizedMode = String(matchingMode || "").toUpperCase();
  return results.filter(
    (record) => String(record?.matching_mode || "").toUpperCase() === normalizedMode,
  );
};

const buildModeSummary = (summaryData, matchingMode) => {
  if (!summaryData || !matchingMode) return summaryData;

  const modeResults = getMatchingModeResults(summaryData, matchingMode);
  const totalMatches = modeResults.length;
  const matchedCount = modeResults.filter((record) =>
    ["FULLY_MATCHED", "MATCHED", "APPROVED"].includes(
      String(record?.status || "").toUpperCase(),
    ),
  ).length;
  const partialCount = modeResults.filter((record) =>
    String(record?.status || "").toUpperCase().includes("PARTIAL"),
  ).length;
  const incompleteCount = modeResults.filter(
    (record) =>
      record?.has_all_documents === false ||
      String(record?.status || "").toUpperCase().includes("INCOMPLETE"),
  ).length;
  const exceptionCount = Math.max(
    totalMatches - matchedCount - partialCount - incompleteCount,
    0,
  );
  const invoiceValues = modeResults.map((record) =>
    toNum(record?.payment_advice_summary?.invoice_total_amount),
  );
  const populatedValues = invoiceValues.filter((value) => value > 0);
  const backendTotalInvoiceValue = getFirstSummaryValue(
    summaryData?.overview?.total_invoice_value,
    summaryData?.invoice_analytics?.total_invoice_value,
    summaryData?.dashboard_metrics?.total_invoice_value,
    summaryData?.analytics_kpis?.total_invoice_value,
    summaryData?.total_invoice_value,
    summaryData?.total_invoice_amount,
  );
  const totalInvoiceValue = populatedValues.length
    ? populatedValues.reduce((sum, value) => sum + value, 0)
    : toNum(backendTotalInvoiceValue);
  const invoiceCount = modeResults.reduce(
    (sum, record) => sum + Math.max(toNum(record?.invoice_count), 1),
    0,
  );
  const paymentAdviceCount = modeResults.filter(
    (record) => record?.payment_advice_summary?.has_payment_advice,
  ).length;
  const periodBuckets = { weekly: new Map(), monthly: new Map(), yearly: new Map() };

  modeResults.forEach((record) => {
    const date = new Date(record?.created_at || "");
    if (Number.isNaN(date.getTime())) return;
    const value = toNum(record?.payment_advice_summary?.invoice_total_amount);
    const entries = [
      ["weekly", `${date.getFullYear()}-W${String(Math.ceil((date.getDate() + date.getDay()) / 7)).padStart(2, "0")}`],
      ["monthly", `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`],
      ["yearly", String(date.getFullYear())],
    ];
    entries.forEach(([period, key]) => {
      const current = periodBuckets[period].get(key) || { period: key, invoice_value: 0, invoice_count: 0 };
      current.invoice_value += value;
      current.invoice_count += 1;
      periodBuckets[period].set(key, current);
    });
  });

  const originalOverview = summaryData?.overview || {};
  const originalMatching = summaryData?.matching_analytics || {};
  const originalInvoice = summaryData?.invoice_analytics || {};
  const modeLabel = matchingMode === "FOUR_WAY" ? "four_way" : "three_way";
  const mismatchReasonCounts = new Map();
  modeResults.forEach((record) => {
    asSummaryArray(record?.mismatch_reasons).forEach((reason) => {
      const label = String(reason || "Unknown");
      mismatchReasonCounts.set(label, (mismatchReasonCounts.get(label) || 0) + 1);
    });
  });
  const modeMismatchReasons = [...mismatchReasonCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return {
    ...summaryData,
    overview: {
      ...originalOverview,
      group_count: totalMatches,
      total_groups: totalMatches,
      total_matching_submissions: totalMatches,
      total_documents_processed:
        invoiceCount + totalMatches * 2 + (matchingMode === "FOUR_WAY" ? paymentAdviceCount : 0),
      total_invoices: invoiceCount,
      total_pos: totalMatches,
      total_grns: totalMatches,
      total_invoice_value: totalInvoiceValue,
      average_invoice_value: invoiceCount
        ? totalInvoiceValue / invoiceCount
        : toNum(
            getFirstSummaryValue(
              originalOverview.average_invoice_value,
              originalInvoice.average_invoice_value,
              summaryData?.average_invoice_value,
            ),
          ),
      processed_document_counts: {
        INVOICE: invoiceCount,
        PO: totalMatches,
        GRN: totalMatches,
        ...(matchingMode === "FOUR_WAY" ? { PAYMENT: paymentAdviceCount } : {}),
      },
    },
    matching_analytics: {
      ...originalMatching,
      total_matches: totalMatches,
      matched_count: matchedCount,
      partial_count: partialCount,
      exception_count: exceptionCount,
      incomplete_count: incompleteCount,
      [`${modeLabel}_count`]: totalMatches,
      [`${modeLabel}_success_rate`]: totalMatches ? (matchedCount / totalMatches) * 100 : 0,
      mode_count: totalMatches,
      mode_success_rate: totalMatches ? (matchedCount / totalMatches) * 100 : 0,
    },
    dashboard_metrics: {
      ...(summaryData?.dashboard_metrics || {}),
      total_matching_done: totalMatches,
      fully_matched_invoices: matchedCount,
      partially_matched_invoices: partialCount,
      invoices_with_exceptions: exceptionCount,
      incomplete_document_invoices: incompleteCount,
      top_3_mismatch_reasons: modeMismatchReasons,
    },
    analytics_kpis: {
      ...(summaryData?.analytics_kpis || {}),
      top_mismatch_reasons: modeMismatchReasons,
    },
    ...(matchingMode === "FOUR_WAY"
      ? {
          vendor_analytics: {},
          po_analytics: {},
          grn_analytics: {},
          item_analytics: {},
          core_reports: {},
          exception_analytics: {
            missing_po_count: 0,
            missing_grn_count: 0,
            tolerance_breach_count: 0,
            price_variances: modeMismatchReasons,
            quantity_variances: {},
            vendor_wise_exceptions: [],
          },
        }
      : {}),
    invoice_analytics: modeResults.length
      ? {
          ...originalInvoice,
          invoice_value_trend: {
            weekly: [...periodBuckets.weekly.values()],
            monthly: [...periodBuckets.monthly.values()],
            yearly: [...periodBuckets.yearly.values()],
          },
          invoice_count_trend: {
            weekly: [...periodBuckets.weekly.values()].map(({ period, invoice_count }) => ({ period, invoice_count })),
            monthly: [...periodBuckets.monthly.values()].map(({ period, invoice_count }) => ({ period, invoice_count })),
            yearly: [...periodBuckets.yearly.values()].map(({ period, invoice_count }) => ({ period, invoice_count })),
          },
          top_invoices: [],
        }
      : originalInvoice,
    results: modeResults,
  };
};

const SummaryTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper
      elevation={4}
      sx={{
        p: 1.4,
        border: `1px solid ${SUMMARY_COLORS.border}`,
        borderRadius: 2,
        minWidth: 210,
        boxShadow: "0 18px 36px rgba(15,23,42,0.16)",
      }}
    >
      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 14, color: SUMMARY_COLORS.text, mb: 0.8 }}>
        {label}
      </Typography>
      <Stack spacing={0.45}>
        {payload.map((entry) => {
          const key = String(entry.dataKey || "").toLowerCase();
          const seriesName = String(entry.name || "").toLowerCase();
          const formatted =
            seriesName.includes("value") ||
            seriesName.includes("amount") ||
            seriesName.includes("price") ||
            key.includes("invoicevalue") ||
            key.includes("purchasevalue") ||
            key.includes("unitprice")
              ? formatSummaryCurrency(entry.value)
              : seriesName.includes("%") ||
                seriesName.includes("rate") ||
                seriesName.includes("percent") ||
                key.includes("utilization")
                ? formatSummaryPercent(entry.value)
                : formatSummaryNumber(entry.value);
          return (
            <Stack key={`${entry.dataKey}-${entry.name}`} direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: entry.color }} />
              <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>
                {entry.name}: {formatted}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

const SummarySection = ({ title, subtitle, children, action }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 2.25,
      borderColor: SUMMARY_COLORS.border,
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.96) 100%)",
      boxShadow: "0 18px 38px rgba(15,23,42,0.07)",
      height: "100%",
      position: "relative",
      overflow: "hidden",
      transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${SUMMARY_COLORS.blue}, ${SUMMARY_COLORS.cyan})`,
      },
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: "#cbd5e1",
        boxShadow: "0 20px 44px rgba(15,23,42,0.09)",
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.6 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 15, color: SUMMARY_COLORS.text }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted, mt: 0.35 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Paper>
);

const SummaryMetricTooltip = ({ rows = [], yearlyRows = [], yearlyTitle = "Year-wise records" }) => (
  <Stack spacing={1}>
    {rows.length ? (
      <Stack spacing={0.65}>
        {rows.map((row) => (
          <Stack key={row.label} direction="row" justifyContent="space-between" spacing={2.5}>
            <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{row.label}</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 14, color: SUMMARY_COLORS.text, textAlign: "right" }}>
              {row.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    ) : null}

    {yearlyRows.length ? (
      <Box sx={{ pt: rows.length ? 0.75 : 0, borderTop: rows.length ? `1px solid ${SUMMARY_COLORS.border}` : "none" }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 14, color: SUMMARY_COLORS.slate, mb: 0.7 }}>
          {yearlyTitle}
        </Typography>
        <Stack spacing={0.65}>
          {yearlyRows.map((row) => (
            <Stack key={row.label} direction="row" justifyContent="space-between" spacing={2.5}>
              <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{row.label}</Typography>
              <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 14, color: SUMMARY_COLORS.text, textAlign: "right" }}>
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    ) : null}
  </Stack>
);

const SummaryMetric = ({ title, value, note, icon, color = SUMMARY_COLORS.blue, tooltip }) => {
  const hasTooltip = Boolean(tooltip);

  return (
    <Tooltip
      title={tooltip || ""}
      arrow
      placement="bottom-start"
      disableHoverListener={!hasTooltip}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "#ffffff",
            color: SUMMARY_COLORS.text,
            border: `1px solid ${SUMMARY_COLORS.border}`,
            boxShadow: "0 18px 36px rgba(15,23,42,0.16)",
            borderRadius: 2,
            p: 1.4,
            maxWidth: 440,
          },
        },
        arrow: { sx: { color: "#ffffff" } },
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.8,
          borderRadius: 2.25,
          borderColor: `${color}33`,
          background: `linear-gradient(145deg, ${color}18 0%, #ffffff 54%, ${color}0a 100%)`,
          minHeight: 126,
          height: "100%",
          boxShadow: "0 16px 34px rgba(15,23,42,0.08)",
          position: "relative",
          overflow: "hidden",
          cursor: hasTooltip ? "help" : "default",
          transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
          "&:hover": {
            transform: hasTooltip ? "translateY(-2px)" : "none",
            borderColor: hasTooltip ? `${color}66` : `${color}33`,
            boxShadow: hasTooltip ? "0 20px 38px rgba(15,23,42,0.1)" : "0 16px 34px rgba(15,23,42,0.08)",
          },
          "&:after": {
            content: '""',
            position: "absolute",
            width: 74,
            height: 74,
            right: -28,
            bottom: -34,
            borderRadius: "50%",
            background: `${color}16`,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{title}</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: { xs: 20, md: 23 }, color: SUMMARY_COLORS.text, mt: 0.8, lineHeight: 1.1 }}>
              {value}
            </Typography>
            {note ? <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted, mt: 0.7 }}>{note}</Typography> : null}
          </Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              background: `linear-gradient(135deg, ${color}1f 0%, #ffffff 100%)`,
              border: `1px solid ${color}24`,
              color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </Paper>
    </Tooltip>
  );
};

const SummaryEmpty = ({ label = "No data available" }) => (
  <Box
    sx={{
      minHeight: 240,
      display: "grid",
      placeItems: "center",
      border: `1px dashed ${SUMMARY_COLORS.border}`,
      borderRadius: 2,
      bgcolor: SUMMARY_COLORS.soft,
    }}
  >
    <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{label}</Typography>
  </Box>
);

const SummaryTopRecordsTable = ({ rows = [], emptyLabel = "No top records available" }) => {
  const visibleRows = asSummaryArray(rows).slice(0, TOP_SUMMARY_RECORD_LIMIT);
  const columns = visibleRows.reduce((acc, row) => {
    Object.keys(asSummaryObject(row)).forEach((key) => {
      if (!acc.includes(key)) acc.push(key);
    });
    return acc;
  }, []);

  if (!visibleRows.length || !columns.length) return <SummaryEmpty label={emptyLabel} />;

  return (
    <TableContainer sx={{ maxHeight: 390, overflowX: "auto" }}>
      <Table size="small" stickyHeader sx={{ minWidth: Math.max(620, columns.length * 150) }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column}
                sx={{
                  fontFamily: GILROY_BOLD,
                  fontSize: 14,
                  color: SUMMARY_COLORS.slate,
                  bgcolor: "#f8fafc",
                  whiteSpace: "nowrap",
                }}
              >
                {formatSummaryFieldLabel(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row, index) => (
            <TableRow key={`summary-top-record-${index}`} hover>
              {columns.map((column) => (
                <TableCell
                  key={`${column}-${index}`}
                  sx={{
                    ...summaryFont,
                    color: SUMMARY_COLORS.text,
                    maxWidth: 240,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={formatSummaryRecordValue(column, row?.[column])}
                >
                  {formatSummaryRecordValue(column, row?.[column])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const flattenSummaryResponse = (value, path = "response", rows = []) => {
  if (value === null || value === undefined) {
    rows.push({ field: path, value: "-" });
    return rows;
  }

  if (Array.isArray(value)) {
    if (!value.length) rows.push({ field: path, value: "[]" });
    value.forEach((item, index) =>
      flattenSummaryResponse(item, `${path}[${index}]`, rows),
    );
    return rows;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) rows.push({ field: path, value: "{}" });
    entries.forEach(([key, childValue]) =>
      flattenSummaryResponse(childValue, `${path}.${key}`, rows),
    );
    return rows;
  }

  rows.push({ field: path, value });
  return rows;
};

const SummaryResponseValuesTable = ({ summaryData }) => {
  const rows = flattenSummaryResponse(summaryData);

  if (!rows.length) return <SummaryEmpty label="No response values available" />;

  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: "auto" }}>
      <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontFamily: GILROY_BOLD, fontSize: 14, color: SUMMARY_COLORS.slate, bgcolor: "#f8fafc", width: "58%" }}>
              Response field
            </TableCell>
            <TableCell sx={{ fontFamily: GILROY_BOLD, fontSize: 14, color: SUMMARY_COLORS.slate, bgcolor: "#f8fafc" }}>
              Value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row.field}-${index}`} hover>
              <TableCell sx={{ ...summaryFont, color: SUMMARY_COLORS.slate, whiteSpace: "nowrap" }}>
                {row.field}
              </TableCell>
              <TableCell sx={{ ...summaryFont, color: SUMMARY_COLORS.text, wordBreak: "break-word" }}>
                {formatSummaryRecordValue(row.field, row.value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SummaryHorizontalBarChart = ({
  rows,
  valueKey,
  labelKey = "label",
  name,
  formatter = formatSummaryNumber,
  color = SUMMARY_COLORS.blue,
  height = 310,
  barSize = 14,
}) => {
  if (!rows.length) return <SummaryEmpty />;
  const chartRows = rows.slice(0, TOP_SUMMARY_RECORD_LIMIT).map((row) => ({
    ...row,
    chartLabel: truncateSummaryLabel(row[labelKey], 30),
    tooltipLabel: row[labelKey],
  }));
  const resolvedHeight = Math.max(height, chartRows.length * 42 + 48);
  return (
    <Box sx={{ height: resolvedHeight }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart
          data={chartRows}
          layout="vertical"
          margin={{ top: 8, right: 22, left: 12, bottom: 8 }}
          barCategoryGap={16}
        >
          <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" />
          <XAxis type="number" tickFormatter={formatter} tick={summaryTick} axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey="chartLabel"
            width={210}
            tick={summaryTick}
            interval={0}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip content={<SummaryTooltip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
          <Bar
            dataKey={valueKey}
            name={name}
            fill={color}
            radius={[0, 8, 8, 0]}
            barSize={barSize}
            minPointSize={chartRows.every((row) => toNum(row[valueKey]) === 0) ? 6 : 0}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const SummaryVerticalBarChart = ({
  rows,
  valueKey,
  labelKey = "label",
  name,
  formatter = formatSummaryNumber,
  color = SUMMARY_COLORS.blue,
  height = 300,
}) => {
  if (!rows.length) return <SummaryEmpty />;
  return (
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart data={rows.slice(0, 12)} margin={{ top: 14, right: 18, left: 0, bottom: 58 }}>
          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
          <XAxis
            dataKey={labelKey}
            angle={-24}
            textAnchor="end"
            height={74}
            tick={summaryTick}
            axisLine={false}
            tickLine={false}
          />
          <YAxis tickFormatter={formatter} tick={summaryTick} axisLine={false} tickLine={false} width={72} />
          <RechartsTooltip content={<SummaryTooltip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
          <Bar dataKey={valueKey} name={name} fill={color} radius={[8, 8, 0, 0]} barSize={26} />
        </RechartsBarChart>
      </ResponsiveContainer>
    </Box>
  );
};

function SummaryAnalyticsDashboard({
  summaryData: baseSummaryData,
  loading,
  exporting,
  dashboardRef,
  exportDashboardAsPDF,
  onRefresh,
  isMobile,
}) {
  const [trendMode, setTrendMode] = useState("monthly");
  const [activeSummaryTab, setActiveSummaryTab] = useState("summary");
  const summaryTabsScrollRef = useRef(null);
  const [summaryTabsScroll, setSummaryTabsScroll] = useState({ left: false, right: false });
  const threeWaySummaryData = useMemo(
    () => buildModeSummary(baseSummaryData, "THREE_WAY"),
    [baseSummaryData],
  );
  const fourWaySummaryData = useMemo(
    () => buildModeSummary(baseSummaryData, "FOUR_WAY"),
    [baseSummaryData],
  );
  const matchingMode =
    !exporting && activeSummaryTab === "fourWay" ? "FOUR_WAY" : "THREE_WAY";
  const summaryData = matchingMode === "FOUR_WAY"
    ? fourWaySummaryData
    : threeWaySummaryData;
  const overview = summaryData?.overview || {};
  const matching = summaryData?.matching_analytics || {};
  const invoice = summaryData?.invoice_analytics || {};
  const vendor = summaryData?.vendor_analytics || {};
  const po = summaryData?.po_analytics || {};
  const grn = summaryData?.grn_analytics || {};
  const exception = summaryData?.exception_analytics || {};
  const item = summaryData?.item_analytics || {};
  const coreReports = summaryData?.core_reports || {};
  const overInvoiceReport = coreReports["06_over_invoicing_over_receipt"] || {};
  const partialDeliveryReport = coreReports["07_partial_delivery_pending_quantity"] || {};
  const openPoReport = coreReports["08_open_po_consumption"] || {};
  const priceHistoryReport = coreReports["11_item_price_history"] || {};
  const apAgingReport = coreReports["12_ap_due_aging"] || {};
  const cashFlowReport = coreReports["13_cash_flow_projection"] || {};
  const grnAccrualReport = coreReports["14_grn_accrual_unbilled_liability"] || {};
  const filters = summaryData?.filters_applied || {};
  const modeLabel = matchingMode === "FOUR_WAY" ? "Four Way" : "Three Way";
  const modeCount = toNum(matching.mode_count ?? matching[matchingMode === "FOUR_WAY" ? "four_way_count" : "three_way_count"]);
  const modeSuccessRate = matching.mode_success_rate ?? matching[matchingMode === "FOUR_WAY" ? "four_way_success_rate" : "three_way_success_rate"];
  const totalInvoiceValue = getSummaryMetricValue(
    [summaryData, baseSummaryData],
    [
      "overview.total_invoice_value",
      "invoice_analytics.total_invoice_value",
      "dashboard_metrics.total_invoice_value",
      "analytics_kpis.total_invoice_value",
      "total_invoice_value",
      "total_invoice_amount",
    ],
  );
  const averageInvoiceValue = getSummaryMetricValue(
    [summaryData, baseSummaryData],
    [
      "overview.average_invoice_value",
      "invoice_analytics.average_invoice_value",
      "dashboard_metrics.average_invoice_value",
      "analytics_kpis.average_invoice_value",
      "average_invoice_value",
    ],
  );
  const highestInvoiceValue = getSummaryMetricValue(
    [summaryData, baseSummaryData],
    [
      "overview.highest_invoice_value",
      "invoice_analytics.highest_invoice_value",
      "dashboard_metrics.highest_invoice_value",
      "analytics_kpis.highest_invoice_value",
      "highest_invoice_value",
    ],
  );
  const lowestInvoiceValue = getSummaryMetricValue(
    [summaryData, baseSummaryData],
    [
      "overview.lowest_invoice_value",
      "invoice_analytics.lowest_invoice_value",
      "dashboard_metrics.lowest_invoice_value",
      "analytics_kpis.lowest_invoice_value",
      "lowest_invoice_value",
    ],
  );

  const updateSummaryTabsScroll = useCallback(() => {
    const element = summaryTabsScrollRef.current;
    if (!element) return;

    const maxScrollLeft = Math.max(element.scrollWidth - element.clientWidth, 0);
    setSummaryTabsScroll({
      left: element.scrollLeft > 4,
      right: maxScrollLeft - element.scrollLeft > 4,
    });
  }, []);

  useEffect(() => {
    const element = summaryTabsScrollRef.current;
    if (!element) return undefined;

    updateSummaryTabsScroll();
    element.addEventListener("scroll", updateSummaryTabsScroll, { passive: true });
    window.addEventListener("resize", updateSummaryTabsScroll);
    return () => {
      element.removeEventListener("scroll", updateSummaryTabsScroll);
      window.removeEventListener("resize", updateSummaryTabsScroll);
    };
  }, [updateSummaryTabsScroll]);

  const scrollSummaryTabs = (direction) => {
    summaryTabsScrollRef.current?.scrollBy({
      left: direction * 260,
      behavior: "smooth",
    });
  };
  const paymentAdvanceSource =
    fourWaySummaryData?.payment_advance_metrics ||
    fourWaySummaryData?.payment_advice_metrics ||
    {};
  const paymentAdvice = {
    ...paymentAdvanceSource,
    payment_advance_record_count:
      paymentAdvanceSource.payment_advance_record_count ??
      paymentAdvanceSource.payment_advice_record_count,
    payment_advance_document_count:
      paymentAdvanceSource.payment_advance_document_count ??
      paymentAdvanceSource.payment_advice_document_count,
    payment_advance_total_amount:
      paymentAdvanceSource.payment_advance_total_amount ??
      paymentAdvanceSource.payment_advice_total_amount,
    monthwise_payment_advance:
      paymentAdvanceSource.monthwise_payment_advance ??
      paymentAdvanceSource.monthwise_payment_advice,
    yearwise_payment_advance:
      paymentAdvanceSource.yearwise_payment_advance ??
      paymentAdvanceSource.yearwise_payment_advice,
  };
  const paymentModeRows = Object.entries(paymentAdvice.payment_mode_breakup || {})
    .map(([name, value]) => ({ name, value: toNum(value) }))
    .filter((row) => row.value > 0);
  const paymentMonthRows = Object.entries(paymentAdvice.monthwise_payment_advance || {})
    .map(([period, value]) => ({
      period,
      recordCount: toNum(value?.record_count),
      documentCount: toNum(value?.document_count),
      paidAmount: toNum(value?.paid_amount),
    }))
    .sort((a, b) => String(a.period).localeCompare(String(b.period)));
  const paymentYearRows = Object.entries(paymentAdvice.yearwise_payment_advance || {})
    .map(([period, value]) => ({
      period,
      recordCount: toNum(value?.record_count),
      documentCount: toNum(value?.document_count),
      paidAmount: toNum(value?.paid_amount),
    }))
    .sort((a, b) => String(a.period).localeCompare(String(b.period)));

  const trendRows = useMemo(
    () =>
      getSummaryPeriodRows(invoice.invoice_value_trend, trendMode).map((row) => ({
        period: trendMode === "monthly" ? formatSummaryPeriod(row.period) : String(row.period || "-"),
        invoiceValue: toNum(row.invoice_value),
        invoiceCount: toNum(row.invoice_count),
      })),
    [invoice.invoice_value_trend, trendMode]
  );

  const matchMix = [
    { name: "Matched", value: toNum(matching.matched_count) },
    { name: "Partial", value: toNum(matching.partial_count) },
    { name: "Exception", value: toNum(matching.exception_count) },
    { name: "Incomplete", value: toNum(matching.incomplete_count) },
  ].filter((row) => row.value > 0);

  const topVendorValueRecords = asSummaryArray(vendor.top_vendors_by_value).slice(0, TOP_SUMMARY_RECORD_LIMIT);
  const topVendorCountRecords = asSummaryArray(vendor.top_vendors_by_count).slice(0, TOP_SUMMARY_RECORD_LIMIT);
  const topInvoiceRecords = asSummaryArray(invoice.top_invoices).slice(0, TOP_SUMMARY_RECORD_LIMIT);
  const itemPurchaseValueRecords = asSummaryArray(item.item_wise_purchase_value).slice(0, TOP_SUMMARY_RECORD_LIMIT);

  const vendorRows = topVendorValueRecords.map((row) => ({
    label: row.vendor || "Unknown",
    value: toNum(row.invoice_value),
    count: toNum(row.invoice_count),
  }));
  const vendorCountRows = topVendorCountRecords.map((row) => ({
    label: row.vendor || "Unknown",
    value: toNum(row.invoice_count),
    invoiceValue: toNum(row.invoice_value),
  }));

  const exceptionRows = asSummaryArray(exception.vendor_wise_exceptions).map((row) => ({
    label: row.vendor || "Unknown",
    value: toNum(row.exception_count),
  }));
  const itemRows = (itemPurchaseValueRecords.length
    ? itemPurchaseValueRecords
    : asSummaryArray(item.top_purchased_items)
        .slice(0, TOP_SUMMARY_RECORD_LIMIT)
  ).map((row) => ({
    label: row.item_name || row.part_number || "Unknown",
    value: toNum(row.purchase_value),
  }));

  const documentCounts = asSummaryObject(overview.processed_document_counts);
  const periods = asSummaryArray(filters.periods_returned).join(", ") || "MONTHLY";
  const yearlyTrend = useMemo(() => {
    const activeTrend = buildSummaryYearlyTrend(summaryData);
    return activeTrend.length ? activeTrend : buildSummaryYearlyTrend(baseSummaryData);
  }, [baseSummaryData, summaryData]);
  const yearWiseInvoiceValueRows = useMemo(
    () =>
      buildYearWiseInvoiceValueRows(baseSummaryData, summaryData).map((row) => ({
        label: row.year,
        value: `${formatSummaryCurrency(row.invoiceValue)} (${formatSummaryNumber(row.invoiceCount)} invoices)`,
      })),
    [baseSummaryData, summaryData],
  );
  const invoiceValueYearRows = yearlyTrend.map((row) => ({
    label: row.year,
    value: `${formatSummaryCompactCurrency(row.invoiceValue)} (${formatSummaryNumber(row.invoiceCount)} invoices)`,
  }));
  const invoiceCountYearRows = yearlyTrend.map((row) => ({
    label: row.year,
    value: formatSummaryNumber(row.invoiceCount),
  }));
  const poValueRecords = asSummaryArray(po.po_wise_invoice_value);
  const overInvoicedPoRecords = poValueRecords.filter(
    (row) => String(row?.status || "").toUpperCase() === "OVER_INVOICED"
  );
  const partialPoRecords = poValueRecords.filter(
    (row) => String(row?.status || "").toUpperCase() === "PARTIALLY_INVOICED"
  );
  const grnInvoiceRecords = asSummaryArray(grn.grn_wise_invoice_value);
  const multipleGrnsPerPoRecords = asSummaryArray(grn.multiple_grns_per_po);
  const priceVarianceRecords = asSummaryArray(exception.price_variances);
  const overInvoiceRecords = asSummaryArray(overInvoiceReport.over_invoicing_rows);
  const overReceiptRecords = asSummaryArray(overInvoiceReport.over_receipt_rows);
  const partialDeliveryRecords = asSummaryArray(partialDeliveryReport.rows).length
    ? asSummaryArray(partialDeliveryReport.rows)
    : partialPoRecords;
  const openPoRecords = asSummaryArray(openPoReport.rows).length
    ? asSummaryArray(openPoReport.rows)
    : poValueRecords;
  const priceHistoryRecords = asSummaryArray(priceHistoryReport.rows).length
    ? asSummaryArray(priceHistoryReport.rows)
    : asSummaryArray(item.price_movement);
  const overInvoiceChartRows = [
    { label: "Over-invoicing", value: overInvoiceRecords.length || overInvoicedPoRecords.length },
    { label: "Over-receipt", value: overReceiptRecords.length },
  ];
  const partialDeliveryChartRows = buildSummaryChartRows(
    partialDeliveryRecords,
    ["po_number", "po", "purchase_order", "label", "name"],
    ["pending_quantity", "pending_qty", "quantity_pending", "invoice_value", "amount", "value"],
  ).length
    ? buildSummaryChartRows(
        partialDeliveryRecords,
        ["po_number", "po", "purchase_order", "label", "name"],
        ["pending_quantity", "pending_qty", "quantity_pending", "invoice_value", "amount", "value"],
      )
    : [{ label: "Partial delivery records", value: 0 }];
  const openPoChartRows = buildSummaryChartRows(
    openPoRecords,
    ["po_number", "po", "purchase_order", "label", "name"],
    ["open_po_exposure", "remaining_value", "open_value", "invoice_value", "amount", "value"],
  ).length
    ? buildSummaryChartRows(
        openPoRecords,
        ["po_number", "po", "purchase_order", "label", "name"],
        ["open_po_exposure", "remaining_value", "open_value", "invoice_value", "amount", "value"],
      )
    : [{ label: "Open PO records", value: 0 }];
  const priceHistoryChartRows = buildSummaryChartRows(
    priceHistoryRecords,
    ["item_name", "part_number", "item", "period", "date", "label", "name"],
    ["unit_price", "average_price", "price", "purchase_value", "invoice_value", "amount", "value"],
  ).length
    ? buildSummaryChartRows(
        priceHistoryRecords,
        ["item_name", "part_number", "item", "period", "date", "label", "name"],
        ["unit_price", "average_price", "price", "purchase_value", "invoice_value", "amount", "value"],
      )
    : [{ label: "Price history records", value: 0 }];
  const apAgingChartRows = Object.entries(apAgingReport.bucket_totals || {}).map(([label, value]) => ({ label, value: toNum(value) }));
  const visibleApAgingChartRows = apAgingChartRows.length
    ? apAgingChartRows
    : [{ label: "No aging value returned", value: 0 }];
  const cashFlowChartRows = Object.entries(cashFlowReport.horizon_totals || {}).map(([label, value]) => ({ label, value: toNum(value) }));
  const visibleCashFlowChartRows = cashFlowChartRows.length
    ? cashFlowChartRows
    : [{ label: "No projection value returned", value: 0 }];
  const quantityVariance = asSummaryObject(exception.quantity_variances);
  const poGrnVarianceRecords = asSummaryArray(
    quantityVariance.po_vs_grn || exception.po_grn_quantity_variances
  );
  const invoiceGrnVarianceRecords = asSummaryArray(
    quantityVariance.invoice_vs_grn || exception.invoice_grn_quantity_variances
  );
  const exceptionSummaryRows = [
    ["Price variances", priceVarianceRecords.length],
    ["PO-GRN quantity variances", poGrnVarianceRecords.length],
    ["Invoice-GRN quantity variances", invoiceGrnVarianceRecords.length],
    ["Missing PO", exception.missing_po_count],
    ["Missing GRN", exception.missing_grn_count],
    ["Tolerance breaches", exception.tolerance_breach_count],
  ];
  const exceptionChartRows = exceptionSummaryRows.map(([label, value]) => ({ label, value: toNum(value) }));
  const missingDocumentRecords =
    asSummaryArray(coreReports["04_missing_document"]?.rows).length
      ? asSummaryArray(coreReports["04_missing_document"]?.rows)
      : asSummaryArray(summaryData?.missing_document_report).length
      ? asSummaryArray(summaryData?.missing_document_report)
      : [
          { document_type: "PO", missing_count: exception.missing_po_count ?? 0 },
          { document_type: "GRN/SRN", missing_count: exception.missing_grn_count ?? 0 },
          { document_type: "Invoice", missing_count: matching.incomplete_count ?? 0 },
        ];
  const missingDocumentChartRows = buildSummaryChartRows(
    missingDocumentRecords,
    ["document_type", "scenario", "label", "name"],
    ["missing_count", "count", "value"],
  );
  const coreDuplicateInvoiceRecords = asSummaryArray(coreReports["05_duplicate_invoice"]?.rows);
  const duplicateInvoiceRecords = coreDuplicateInvoiceRecords.length
    ? coreDuplicateInvoiceRecords
    : asSummaryArray(summaryData?.duplicate_invoice_report || invoice.duplicate_invoices);
  const apAgingRecords = asSummaryArray(
    apAgingReport.rows?.length
      ? apAgingReport.rows
      : summaryData?.accounts_payable_due_aging || summaryData?.ap_due_aging
  );
  const cashFlowRecords = asSummaryArray(
    cashFlowReport.rows?.length
      ? cashFlowReport.rows
      : summaryData?.cash_flow_projection || summaryData?.cashflow_projection
  );
  const grnAccrualRecords = asSummaryArray(
    grnAccrualReport.rows?.length
      ? grnAccrualReport.rows
      : summaryData?.grn_srn_accrual || summaryData?.unbilled_liability_report
  );
  const duplicateChartRows = [{ label: "Duplicate invoice records", value: duplicateInvoiceRecords.length }];
  const grnAccrualChartRows = [
    { label: "Partial GRNs", value: toNum(grn.partial_grn_count) },
    { label: "Complete GRNs", value: toNum(grn.complete_grn_count) },
    { label: "Accrual records", value: grnAccrualRecords.length },
  ];
  const summaryTabs = [
    { id: "summary", label: "Summary" },
    { id: "lineItem", label: "Line Item" },
    { id: "spend", label: "Spend" },
    { id: "exceptions", label: "Exceptions" },
    { id: "missingDocs", label: "Missing Docs" },
    { id: "duplicates", label: "Duplicates" },
    { id: "overInvoicing", label: "Over Invoice" },
    { id: "partialDelivery", label: "Partial Delivery" },
    { id: "openPo", label: "Open PO" },
    { id: "vendor", label: "Supplierwise Outflow" },
    // { id: "priceHistory", label: "Price History" },
    { id: "apAging", label: "AP Aging" },
    { id: "cashFlow", label: "Cash Flow" },
    { id: "grnAccrual", label: "GRN Accrual" },
    { id: "fourWay", label: "Payment Advice" },
  ];
  const shouldRenderSummaryTab = (...ids) =>
    exporting ||
    (activeSummaryTab === "fourWay"
      ? ids.includes("fourWay")
      : ids.includes(activeSummaryTab));

  if (loading) return <LoadingSkeleton />;

  return (
    <Box sx={{ width: "100%", bgcolor: "#f4f7fb", pt: 3 }}>
      <Box
        component="main"
        ref={dashboardRef}
        data-dashboard-export-root="true"
        sx={{
          width: "100%",
          minWidth: 0,
          px: { xs: 2, sm: 3 },
          pb: 4,
          ...(exporting ? { width: `${PDF_EXPORT_WIDTH}px`, minWidth: `${PDF_EXPORT_WIDTH}px`, overflow: "visible" } : {}),
        }}
      >
        <Paper
          data-export-section
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.75 },
            mb: 2.5,
            borderRadius: 3,
            borderColor: SUMMARY_COLORS.border,
            background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 56%, #f8fafc 100%)",
            boxShadow: "0 22px 52px rgba(15,23,42,0.08)",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
            <Box>
              <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: isMobile ? 20 : 24, color: SUMMARY_COLORS.text }}>
                Matching Analytics Dashboard
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
            >
              <Chip label={`Periods: ${periods}`} size="large" sx={{ ...summaryFont, fontSize: 14, bgcolor: "#eef6ff", border: `1px solid ${SUMMARY_COLORS.border}` }} />
              {/* <Chip label={`Date basis: ${overview.analytics_date_basis || "-"}`} size="large" sx={{ ...summaryFont, fontSize: 14, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }} /> */}
              <Chip label={overview.reprocessed_submissions_included ? "Reprocessed included" : "Original submissions"} size="large" sx={{ ...summaryFont, fontSize: 14, bgcolor: "#fff7ed", border: "1px solid #fed7aa" }} />
              <Button data-export-ignore variant="contained" startIcon={<Download />} onClick={exportDashboardAsPDF} disabled={exporting} sx={{ textTransform: "none", borderRadius: 2, fontFamily: GILROY_BOLD, fontSize: 14, boxShadow: "none" }}>
                {exporting ? "Exporting..." : "Export PDF"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          data-export-ignore
          elevation={0}
          sx={{
            p: { xs: 0.75, sm: 1 },
            mb: 2.5,
            borderRadius: 3,
            border: "1px solid #244b82",
            background:
              "linear-gradient(120deg, #102b52 0%, #1f5b9f 52%, #174782 100%)",
            boxShadow: "0 14px 30px rgba(18, 55, 101, 0.22)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              aria-label="Scroll matching dashboard tabs left"
              size="small"
              onClick={() => scrollSummaryTabs(-1)}
              disabled={!summaryTabsScroll.left}
              sx={{
                flex: "0 0 auto",
                color: "#fff",
                opacity: summaryTabsScroll.left ? 0.9 : 0.3,
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Box
              ref={summaryTabsScrollRef}
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ minWidth: "max-content", alignItems: "center" }}
              >
                {summaryTabs.map((tab, index) => {
                  const active = activeSummaryTab === tab.id;
                  return (
                    <Button
                      key={tab.id}
                      onClick={() => setActiveSummaryTab(tab.id)}
                      sx={{
                        position: "relative",
                        textTransform: "none",
                        borderRadius: 2.5,
                        px: { xs: 1.75, sm: 2.25 },
                        minHeight: 46,
                        fontFamily: active ? GILROY_BOLD : GILROY_MEDIUM,
                        fontSize: "15px",
                        letterSpacing: "0.01em",
                        color: active ? "#143864" : "rgba(255,255,255,0.9)",
                        bgcolor: active ? "#fff" : "rgba(255,255,255,0.1)",
                        border: active
                          ? "1px solid rgba(255,255,255,0.9)"
                          : "1px solid rgba(255,255,255,0.12)",
                        boxShadow: active
                          ? "0 8px 18px rgba(4,22,49,0.26)"
                          : "none",
                        transition: "all .2s ease",
                        "&::before": {
                          content: `"${String(index + 1).padStart(2, "0")}"`,
                          mr: 0.8,
                          fontSize: 10,
                          fontFamily: GILROY_BOLD,
                          opacity: active ? 0.6 : 0.7,
                        },
                        "&:hover": {
                          bgcolor: active ? "#fff" : "rgba(255,255,255,0.2)",
                          borderColor: "rgba(255,255,255,0.36)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {tab.label}
                    </Button>
                  );
                })}
              </Stack>
            </Box>
            <IconButton
              aria-label="Scroll matching dashboard tabs right"
              size="small"
              onClick={() => scrollSummaryTabs(1)}
              disabled={!summaryTabsScroll.right}
              sx={{
                flex: "0 0 auto",
                color: "#fff",
                opacity: summaryTabsScroll.right ? 0.9 : 0.3,
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Paper>

        {shouldRenderSummaryTab("fourWay") && (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: { xs: 20, md: 24 }, color: SUMMARY_COLORS.text }}>
                Payment Advice Overview
              </Typography>
              <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted, mt: 0.4 }}>
                Four-way matching payment coverage, settlement activity, and payment modes.
              </Typography>
            </Box>
            <Grid container spacing={2} mb={2.5} data-export-section>
              {[
                ["Four Way Matching", paymentAdvice.four_way_matching_count, "Matching records", <AssignmentTurnedIn />, SUMMARY_COLORS.blue],
                ["Payment Advice Records", paymentAdvice.payment_advance_record_count, "Records with payment advice", <Payment />, SUMMARY_COLORS.purple],
                ["Payment Advice Documents", paymentAdvice.payment_advance_document_count, "Uploaded payment documents", <Receipt />, SUMMARY_COLORS.cyan],
                ["Invoice Total Amount", formatSummaryCurrency(paymentAdvice.invoice_total_amount), "Invoice value in four-way records", <Receipt />, SUMMARY_COLORS.indigo],
                ["Paid Amount", formatSummaryCurrency(paymentAdvice.payment_advance_total_amount), "Total recorded payment advice amount", <Payment />, SUMMARY_COLORS.green],
                ["Outstanding Amount", formatSummaryCurrency(paymentAdvice.outstanding_amount), "Invoice value still outstanding", <Schedule />, SUMMARY_COLORS.red],
                ["Payment Coverage", `${formatSummaryNumber(paymentAdvice.payment_coverage_percent)}%`, `${formatSummaryNumber(paymentAdvice.partially_paid_count)} partially paid`, <CheckCircle />, SUMMARY_COLORS.amber],
                ["Fully Paid", paymentAdvice.fully_paid_count, "Fully settled records", <CheckCircle />, SUMMARY_COLORS.green],
                ["Partially Paid", paymentAdvice.partially_paid_count, "Records with partial settlement", <Payment />, SUMMARY_COLORS.amber],
                ["Unpaid", paymentAdvice.unpaid_count, "Records without payment", <Schedule />, SUMMARY_COLORS.red],
                ["Advance Payments", paymentAdvice.advance_payment_count, "Records with advance payment", <TrendingUp />, SUMMARY_COLORS.purple],
              ].map(([title, value, note, icon, color]) => (
                <Grid item xs={12} sm={6} lg={4} key={title}>
                  <SummaryMetric
                    title={title}
                    value={typeof value === "number" ? formatSummaryNumber(value) : value}
                    note={note}
                    icon={icon}
                    color={color}
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={2.5} mb={2.5} data-export-section>
              <Grid item xs={12} lg={5}>
                <SummarySection title="Payment Mode Breakup" subtitle="Payment advice transactions by mode">
                  {paymentModeRows.length ? (
                    <Box sx={{ height: 360, position: "relative" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie data={paymentModeRows} dataKey="value" nameKey="name" innerRadius={72} outerRadius={112} paddingAngle={4} cornerRadius={8}>
                            {paymentModeRows.map((row, index) => (
                              <Cell key={row.name} fill={summarySeries[index % summarySeries.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip content={<SummaryTooltip />} />
                          <Legend
                            verticalAlign="bottom"
                            wrapperStyle={{
                              fontFamily: GILROY_BOLD,
                              fontSize: 17,
                              color: SUMMARY_COLORS.text,
                              paddingTop: 18,
                            }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <SummaryEmpty label="No payment mode data available" />}
                </SummarySection>
              </Grid>
              <Grid item xs={12} lg={7}>
                <SummarySection title="Payment Settlement Snapshot" subtitle="Paid amount and document coverage for the selected payment advice data">
                  <Grid container spacing={1.2}>
                    {[
                      ["Payment modes", paymentModeRows.length],
                      ["Paid amount", formatSummaryCurrency(paymentAdvice.payment_advance_total_amount)],
                      ["Advance payments", formatSummaryNumber(paymentAdvice.advance_payment_count)],
                      ["Coverage", `${formatSummaryNumber(paymentAdvice.payment_coverage_percent)}%`],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                          <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{label}</Typography>
                          <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text, mt: 0.4 }}>{typeof value === "number" ? formatSummaryNumber(value) : value}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </SummarySection>
              </Grid>
            </Grid>
            <Grid container spacing={2.5} mb={2.5} data-export-section>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Yearly Payment Advice Trend" subtitle="Separate yearly view of paid amount and payment documents">
                  {paymentYearRows.length ? (
                    <Box sx={{ height: 340 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={paymentYearRows} margin={{ top: 18, right: 20, left: 8, bottom: 12 }}>
                          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="period" tick={summaryTick} axisLine={false} tickLine={false} />
                          <YAxis yAxisId="amount" tickFormatter={formatSummaryCompactCurrency} tick={summaryTick} axisLine={false} tickLine={false} width={82} />
                          <YAxis yAxisId="count" orientation="right" allowDecimals={false} tick={summaryTick} axisLine={false} tickLine={false} width={42} />
                          <RechartsTooltip content={<SummaryTooltip />} />
                          <Legend wrapperStyle={{ fontFamily: GILROY_MEDIUM, fontSize: 14 }} />
                          <Line yAxisId="amount" type="monotone" dataKey="paidAmount" name="Paid Amount" stroke={SUMMARY_COLORS.green} strokeWidth={3} dot={{ r: 5, fill: SUMMARY_COLORS.green }} activeDot={{ r: 7 }} />
                          <Line yAxisId="count" type="monotone" dataKey="documentCount" name="Documents" stroke={SUMMARY_COLORS.blue} strokeWidth={3} dot={{ r: 5, fill: SUMMARY_COLORS.blue }} activeDot={{ r: 7 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <SummaryEmpty label="No yearly payment advice data available" />}
                </SummarySection>
              </Grid>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Monthly Payment Advice Trend" subtitle="Separate monthly view of paid amount and payment documents">
                  {paymentMonthRows.length ? (
                    <Box sx={{ height: 340 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={paymentMonthRows} margin={{ top: 18, right: 20, left: 8, bottom: 12 }}>
                          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="period" tick={summaryTick} axisLine={false} tickLine={false} />
                          <YAxis yAxisId="amount" tickFormatter={formatSummaryCompactCurrency} tick={summaryTick} axisLine={false} tickLine={false} width={82} />
                          <YAxis yAxisId="count" orientation="right" allowDecimals={false} tick={summaryTick} axisLine={false} tickLine={false} width={42} />
                          <RechartsTooltip content={<SummaryTooltip />} />
                          <Legend wrapperStyle={{ fontFamily: GILROY_MEDIUM, fontSize: 14 }} />
                          <Line yAxisId="amount" type="monotone" dataKey="paidAmount" name="Paid Amount" stroke={SUMMARY_COLORS.purple} strokeWidth={3} dot={{ r: 5, fill: SUMMARY_COLORS.purple }} activeDot={{ r: 7 }} />
                          <Line yAxisId="count" type="monotone" dataKey="documentCount" name="Documents" stroke={SUMMARY_COLORS.cyan} strokeWidth={3} dot={{ r: 5, fill: SUMMARY_COLORS.cyan }} activeDot={{ r: 7 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <SummaryEmpty label="No monthly payment advice data available" />}
                </SummarySection>
              </Grid>
            </Grid>
          </>
        )}

        {shouldRenderSummaryTab("summary") && (
          <>
            <Grid container spacing={2} mb={2.5} data-export-section>
              <Grid item xs={12} sm={6} lg={3}>
                <SummaryMetric
                  title="Total Invoice Value"
                  value={formatSummaryCompactCurrency(totalInvoiceValue)}
                  note={`${formatSummaryNumber(overview.total_invoices)} invoices, avg ${formatSummaryCompactCurrency(averageInvoiceValue)}`}
                  icon={<Receipt />}
                  color={SUMMARY_COLORS.blue}
                  tooltip={<SummaryMetricTooltip rows={[
                    { label: "Total value", value: formatSummaryCurrency(totalInvoiceValue) },
                    { label: "Highest invoice", value: formatSummaryCurrency(highestInvoiceValue) },
                    { label: "Lowest invoice", value: formatSummaryCurrency(lowestInvoiceValue) },
                  ]} yearlyRows={yearWiseInvoiceValueRows.length ? yearWiseInvoiceValueRows : invoiceValueYearRows} yearlyTitle="Year-wise invoice value" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SummaryMetric
                  title="Documents Processed"
                  value={formatSummaryNumber(overview.total_documents_processed)}
                  note={`${formatSummaryNumber(documentCounts.INVOICE)} invoices, ${formatSummaryNumber(documentCounts.PO)} POs, ${formatSummaryNumber(documentCounts.GRN)} GRNs${matchingMode === "FOUR_WAY" ? `, ${formatSummaryNumber(documentCounts.PAYMENT)} payment advices` : ""}`}
                  icon={<AssignmentTurnedIn />}
                  color={SUMMARY_COLORS.cyan}
                  tooltip={<SummaryMetricTooltip rows={[
                    { label: "Invoices", value: formatSummaryNumber(documentCounts.INVOICE) },
                    { label: "POs", value: formatSummaryNumber(documentCounts.PO) },
                    { label: "GRNs", value: formatSummaryNumber(documentCounts.GRN) },
                    ...(matchingMode === "FOUR_WAY" ? [{ label: "Payment advices", value: formatSummaryNumber(documentCounts.PAYMENT) }] : []),
                  ]} yearlyRows={invoiceCountYearRows} />}
                />
              </Grid>
              {matchingMode === "FOUR_WAY" && (
                <Grid item xs={12} sm={6} lg={3}>
                  <SummaryMetric
                    title="Payment Advice Coverage"
                    value={`${formatSummaryNumber(documentCounts.PAYMENT)} / ${formatSummaryNumber(modeCount)}`}
                    note="Four-way groups with payment advice data"
                    icon={<Payment />}
                    color={SUMMARY_COLORS.purple}
                    tooltip={<SummaryMetricTooltip rows={[
                      { label: "Groups", value: formatSummaryNumber(modeCount) },
                      { label: "Payment advices", value: formatSummaryNumber(documentCounts.PAYMENT) },
                    ]} />}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} lg={3}>
                <SummaryMetric
                  title={`${modeLabel} Success`}
                  value={formatSummaryPercent(modeSuccessRate)}
                  note={`${formatSummaryNumber(modeCount)} ${modeLabel.toLowerCase()} groups from ${formatSummaryNumber(matching.total_matches)} matches`}
                  icon={<CheckCircle />}
                  color={SUMMARY_COLORS.green}
                  tooltip={<SummaryMetricTooltip rows={[
                    { label: `${modeLabel} success`, value: formatSummaryPercent(modeSuccessRate) },
                    ...(matchingMode === "FOUR_WAY" ? [] : [{ label: "Two-way success", value: formatSummaryPercent(matching.two_way_success_rate) }]),
                    { label: "Matched", value: formatSummaryNumber(matching.matched_count) },
                    { label: "Partial", value: formatSummaryNumber(matching.partial_count) },
                    { label: "Incomplete", value: formatSummaryNumber(matching.incomplete_count) },
                  ]} yearlyRows={invoiceCountYearRows} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SummaryMetric
                  title="Exceptions"
                  value={formatSummaryNumber(matching.exception_count)}
                  note={`${formatSummaryNumber(exception.tolerance_breach_count)} tolerance breaches`}
                  icon={<Warning />}
                  color={SUMMARY_COLORS.red}
                  tooltip={<SummaryMetricTooltip rows={exceptionSummaryRows.map(([label, value]) => ({
                    label,
                    value: formatSummaryNumber(value),
                  }))} yearlyRows={invoiceCountYearRows} />}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2.5} data-export-section>
              {[
                ["Groups", overview.group_count],
                ["Matching Submissions", overview.total_matching_submissions],
                ["POs", overview.total_pos],
                ["GRNs", overview.total_grns],
                ["Fully Invoiced POs", po.fully_invoiced_count],
                ["Partial GRNs", grn.partial_grn_count],
              ].map(([label, value]) => (
                <Grid item xs={6} md={2} key={label}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: "#fff", height: "100%" }}>
                    <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{label}</Typography>
                    <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 20, color: SUMMARY_COLORS.text, mt: 0.4 }}>
                      {formatSummaryNumber(value)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {shouldRenderSummaryTab("lineItem") && (
          <Grid container spacing={2.5} mb={2.5} data-export-section>
            <Grid item xs={12} lg={4}>
              <SummarySection title="Matching Mix" subtitle={`${formatSummaryNumber(matching.total_matches)} total matches`}>
                <Box sx={{ height: 300, position: "relative" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie data={matchMix} dataKey="value" nameKey="name" innerRadius={68} outerRadius={105} paddingAngle={4} cornerRadius={8}>
                        {matchMix.map((entry, index) => (
                          <Cell key={entry.name} fill={summarySeries[index % summarySeries.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<SummaryTooltip />} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                  <Stack spacing={0.65} sx={{ position: "absolute", right: 0, bottom: 0 }}>
                    {matchMix.map((row, index) => (
                      <Typography key={row.name} sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>
                        <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", mr: 0.75, bgcolor: summarySeries[index % summarySeries.length] }} />
                        {row.name}: {formatSummaryNumber(row.value)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </SummarySection>
            </Grid>
            <Grid item xs={12} lg={8}>
              <SummarySection title="Matching Summary" subtitle={`${modeLabel} outcomes and document coverage`}>
                <Grid container spacing={1.5}>
                  {[
                    ["Total matches", matching.total_matches],
                    [`${modeLabel} count`, modeCount],
                    ["Matched", matching.matched_count],
                    ["Partial", matching.partial_count],
                    ["Exception", matching.exception_count],
                    ["Incomplete", matching.incomplete_count],
                    [`${modeLabel} success`, formatSummaryPercent(modeSuccessRate)],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={label}>
                      <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                        <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{label}</Typography>
                        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text }}>
                          {typeof value === "string" ? value : formatSummaryNumber(value)}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </SummarySection>
            </Grid>
          </Grid>
        )}

        {shouldRenderSummaryTab("spend") && (
          <>
            <Grid container spacing={2.5} mb={2.5} data-export-section>
              <Grid item xs={12} lg={8}>
                <SummarySection
                  title="Invoice Value Trend"
                  subtitle="Weekly, monthly and yearly invoice movement"
                  action={
                    <Stack direction="row" spacing={1} data-export-ignore>
                      {["weekly", "monthly", "yearly"].map((mode) => (
                        <Button
                          key={mode}
                          size="small"
                          variant={trendMode === mode ? "contained" : "outlined"}
                          onClick={() => setTrendMode(mode)}
                          sx={{ textTransform: "none", borderRadius: 2, fontFamily: GILROY_MEDIUM, fontSize: 14, minHeight: 32 }}
                        >
                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </Button>
                      ))}
                    </Stack>
                  }
                >
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={trendRows} margin={{ top: 12, right: 20, left: 0, bottom: 8 }}>
                        <defs>
                          <linearGradient id="p2pInvoiceValueArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={SUMMARY_COLORS.blue} stopOpacity={0.34} />
                            <stop offset="100%" stopColor={SUMMARY_COLORS.blue} stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#dbeafe" />
                        <XAxis dataKey="period" tick={summaryTick} />
                        <YAxis yAxisId="value" tickFormatter={formatSummaryCompactCurrency} tick={summaryTick} width={76} />
                        <YAxis yAxisId="count" orientation="right" allowDecimals={false} tick={summaryTick} width={40} />
                        <RechartsTooltip content={<SummaryTooltip />} />
                        <Area yAxisId="value" type="monotone" dataKey="invoiceValue" name="Invoice Value" stroke={SUMMARY_COLORS.blue} strokeWidth={3} fill="url(#p2pInvoiceValueArea)" />
                        <Bar yAxisId="count" dataKey="invoiceCount" name="Invoice Count" fill={SUMMARY_COLORS.purple} radius={[8, 8, 0, 0]} barSize={18} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </SummarySection>
              </Grid>
              <Grid item xs={12} lg={4}>
                <SummarySection title="Invoice Metrics" subtitle="Value, count and range">
                  <Stack spacing={1.2}>
                    {[
                      ["Total invoice value", formatSummaryCurrency(totalInvoiceValue)],
                      ["Average invoice value", formatSummaryCurrency(averageInvoiceValue)],
                      ["Highest invoice value", formatSummaryCurrency(highestInvoiceValue)],
                      ["Lowest invoice value", formatSummaryCurrency(lowestInvoiceValue)],
                      ["Total invoices", formatSummaryNumber(overview.total_invoices)],
                    ].map(([label, value]) => (
                      <Paper key={label} variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                        <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{label}</Typography>
                        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text }}>{value}</Typography>
                      </Paper>
                    ))}
                  </Stack>
                </SummarySection>
              </Grid>
            </Grid>
            <Grid container spacing={2.5} mb={2.5} data-export-section>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Top 10 Invoices" subtitle="From invoice_analytics.top_invoices">
                  <SummaryTopRecordsTable rows={topInvoiceRecords} emptyLabel="No top invoices available" />
                </SummarySection>
              </Grid>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Top 10 Item Purchase Value" subtitle="From item_analytics.item_wise_purchase_value">
                  <SummaryHorizontalBarChart rows={itemRows} valueKey="value" name="Purchase Value" formatter={formatSummaryCompactCurrency} color={SUMMARY_COLORS.purple} />
                </SummarySection>
              </Grid>
              <Grid item xs={12}>
                <SummarySection title="Item Purchase Value Records" subtitle="All fields returned in item_wise_purchase_value">
                  <SummaryTopRecordsTable rows={itemPurchaseValueRecords} emptyLabel="No item-wise purchase value records available" />
                </SummarySection>
              </Grid>
            </Grid>
          </>
        )}

        {shouldRenderSummaryTab("vendor") && (
          <>
            <Grid container spacing={2.5} mb={2.5} data-export-section>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Top 10 Supplier by Value" subtitle={`Top 10 concentration: ${formatSummaryPercent(vendor.vendor_concentration?.top_10_percentage)}`}>
                  <SummaryHorizontalBarChart rows={vendorRows} valueKey="value" name="Invoice Value" formatter={formatSummaryCompactCurrency} color={SUMMARY_COLORS.cyan} />
                </SummarySection>
              </Grid>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Top 10 Supplier by Count" subtitle="Highest invoice counts in this response">
                  <SummaryHorizontalBarChart rows={vendorCountRows} valueKey="value" name="Invoice Count" color={SUMMARY_COLORS.green} />
                </SummarySection>
              </Grid>
            </Grid>
            <Grid container spacing={2.5} mb={2.5} data-export-section>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Top 10 Supplier Value Records" subtitle="From vendor_analytics.top_vendors_by_value">
                  <SummaryTopRecordsTable rows={topVendorValueRecords} emptyLabel="No top suppliers by value available" />
                </SummarySection>
              </Grid>
              <Grid item xs={12} lg={6}>
                <SummarySection title="Top 10 Supplier Count Records" subtitle="From vendor_analytics.top_vendors_by_count">
                  <SummaryTopRecordsTable rows={topVendorCountRecords} emptyLabel="No top suppliers by count available" />
                </SummarySection>
              </Grid>
            </Grid>
          </>
        )}

        {shouldRenderSummaryTab("overInvoicing") && (
          <Grid container spacing={2.5} mb={2.5} data-export-section>
            <Grid item xs={12} lg={6}>
              <SummarySection title="Over-Invoice Signals" subtitle="Count of over-invoicing and over-receipt cases">
                <SummaryHorizontalBarChart rows={overInvoiceChartRows} valueKey="value" name="Cases" color={SUMMARY_COLORS.red} />
              </SummarySection>
            </Grid>
            <Grid item xs={12} lg={6}>
              <SummarySection title="Over-Invoicing Records" subtitle="From core_reports.06_over_invoicing_over_receipt">
                <SummaryTopRecordsTable rows={overInvoiceRecords.length ? overInvoiceRecords : overInvoicedPoRecords} emptyLabel="No over-invoicing records available" />
              </SummarySection>
            </Grid>
            <Grid item xs={12}>
              <SummarySection title="Over-Receipt Records" subtitle="Purchase orders where received quantity exceeds the PO quantity">
                <SummaryTopRecordsTable rows={overReceiptRecords} emptyLabel="No over-receipt records available" />
              </SummarySection>
            </Grid>
          </Grid>
        )}

        {shouldRenderSummaryTab("partialDelivery") && (
          <Grid container spacing={2.5} mb={2.5} data-export-section>
            <Grid item xs={12} lg={5}>
              <SummarySection title="Pending Delivery Overview" subtitle="Pending quantity or value by purchase order">
                <SummaryHorizontalBarChart rows={partialDeliveryChartRows} valueKey="value" name="Pending" color={SUMMARY_COLORS.amber} />
              </SummarySection>
            </Grid>
            <Grid item xs={12} lg={7}>
              <SummarySection title="Partial Delivery & Pending Quantity" subtitle="From core_reports.07_partial_delivery_pending_quantity">
                <SummaryTopRecordsTable rows={partialDeliveryRecords} emptyLabel="No partial-delivery records available" />
              </SummarySection>
            </Grid>
          </Grid>
        )}

        {shouldRenderSummaryTab("openPo") && (
          <Grid container spacing={2.5} mb={2.5} data-export-section>
            <Grid item xs={12} lg={4}>
              <SummarySection title="Open PO Exposure" subtitle="From core_reports.08_open_po_consumption">
                <SummaryMetric title="Open PO exposure" value={formatSummaryCurrency(openPoReport.open_po_exposure ?? po.open_po_exposure)} note="Unconsumed purchase-order value" icon={<PendingActions />} color={SUMMARY_COLORS.amber} />
              </SummarySection>
            </Grid>
            <Grid item xs={12} lg={8}>
              <SummarySection title="Open PO Consumption Chart" subtitle="Open purchase orders and their current consumption">
                <SummaryHorizontalBarChart rows={openPoChartRows} valueKey="value" name="Open value" formatter={formatSummaryCompactCurrency} color={SUMMARY_COLORS.blue} />
              </SummarySection>
            </Grid>
            <Grid item xs={12}>
              <SummarySection title="Open PO Records" subtitle="Detailed open purchase-order records">
                <SummaryTopRecordsTable rows={openPoRecords} emptyLabel="No open PO records available" />
              </SummarySection>
            </Grid>
          </Grid>
        )}

        {shouldRenderSummaryTab("grnAccrual") && (
          <Grid container spacing={2.5} mb={2.5} data-export-section>
            <Grid item xs={12} lg={4}>
              <SummarySection title="GRN/SRN Accrual & Unbilled Liability" subtitle="Completion, multi-GRN and unbilled receipt signals">
                <SummaryHorizontalBarChart rows={grnAccrualChartRows} valueKey="value" name="GRN count" color={SUMMARY_COLORS.green} />
                <Stack spacing={1.2}>
                  {[
                    ["Partial GRNs", grn.partial_grn_count],
                    ["Complete GRNs", grn.complete_grn_count],
                    ["Multiple GRNs per PO", multipleGrnsPerPoRecords.length],
                    ["Accrual records", grnAccrualRecords.length],
                  ].map(([label, value]) => (
                    <Paper key={label} variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                      <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{label}</Typography>
                      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text }}>{formatSummaryNumber(value)}</Typography>
                    </Paper>
                  ))}
                </Stack>
              </SummarySection>
            </Grid>
            <Grid item xs={12} lg={8}>
              <SummarySection title="Multiple GRNs per PO" subtitle="From grn_analytics.multiple_grns_per_po">
                <SummaryTopRecordsTable rows={multipleGrnsPerPoRecords} emptyLabel="No multiple-GRN PO records available" />
              </SummarySection>
            </Grid>
            <Grid item xs={12}>
              <SummarySection title="GRN Wise Invoice Value" subtitle="From grn_analytics.grn_wise_invoice_value">
                <SummaryTopRecordsTable rows={grnInvoiceRecords} emptyLabel="No GRN-wise invoice records available" />
              </SummarySection>
            </Grid>
            <Grid item xs={12}>
              <SummarySection title="Unbilled Liability Records" subtitle="From grn_srn_accrual / unbilled_liability_report when available">
                <SummaryTopRecordsTable rows={grnAccrualRecords} emptyLabel="No GRN/SRN accrual records available" />
              </SummarySection>
            </Grid>
          </Grid>
        )}

        {shouldRenderSummaryTab("exceptions", "missingDocs", "duplicates", "priceHistory", "apAging", "cashFlow") && (
          <>
            {shouldRenderSummaryTab("exceptions") && (
              <Grid container spacing={2.5} mb={2.5} data-export-section>
              <Grid item xs={12}>
                <SummarySection
                  title={
                    exporting
                      ? "Exception Signals"
                      : activeSummaryTab === "missingDocs"
                      ? "Missing Document Report"
                      : activeSummaryTab === "duplicates"
                      ? "Duplicate Invoice Report"
                      : activeSummaryTab === "priceHistory"
                      ? "Item Price History & Price Movement"
                      : activeSummaryTab === "apAging"
                      ? "Accounts Payable Due & Aging"
                      : activeSummaryTab === "cashFlow"
                      ? "Cash-Flow Projection"
                      : "Exception Signals"
                  }
                  subtitle="Variance, missing document and report-specific counts"
                >
                  <SummaryHorizontalBarChart rows={exceptionChartRows} valueKey="value" name="Exception count" color={SUMMARY_COLORS.red} />
                  <Grid container spacing={1.5}>
                    {[
                      ...exceptionSummaryRows,
                      ["Missing document records", missingDocumentRecords.length],
                      ["Duplicate invoice records", duplicateInvoiceRecords.length],
                      ["AP aging records", apAgingRecords.length],
                      ["Cash-flow records", cashFlowRecords.length],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={label}>
                        <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                          <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{label}</Typography>
                          <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text }}>{formatSummaryNumber(value)}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </SummarySection>
              </Grid>
              </Grid>
            )}
            <Grid container spacing={2.5} mb={2.5} data-export-section>
              {shouldRenderSummaryTab("exceptions") && (
                <>
                  <Grid item xs={12} lg={6}>
                    <SummarySection title="Price Variances" subtitle="From exception_analytics.price_variances">
                      <SummaryTopRecordsTable rows={priceVarianceRecords} emptyLabel="No price variances available" />
                    </SummarySection>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <SummarySection title="PO vs GRN Quantity Variances" subtitle="From exception_analytics.quantity_variances.po_vs_grn">
                      <SummaryTopRecordsTable rows={poGrnVarianceRecords} emptyLabel="No PO-GRN variances available" />
                    </SummarySection>
                  </Grid>
                  <Grid item xs={12}>
                    <SummarySection title="Invoice vs GRN Quantity Variances" subtitle="From exception_analytics.quantity_variances.invoice_vs_grn">
                      <SummaryTopRecordsTable rows={invoiceGrnVarianceRecords} emptyLabel="No invoice-GRN variances available" />
                    </SummarySection>
                  </Grid>
                </>
              )}
              {shouldRenderSummaryTab("missingDocs") && (
                <Grid item xs={12}>
                  <SummarySection title="Missing Document Report" subtitle="Invoice, PO and GRN/SRN missing signals">
                    <SummaryHorizontalBarChart rows={missingDocumentChartRows} valueKey="value" name="Missing documents" color={SUMMARY_COLORS.amber} />
                    <SummaryTopRecordsTable rows={missingDocumentRecords} emptyLabel="No missing document records available" />
                  </SummarySection>
                </Grid>
              )}
              {shouldRenderSummaryTab("duplicates") && (
                <Grid item xs={12}>
                  <SummarySection title="Duplicate Invoice Report" subtitle="Potential duplicate invoices when returned by backend">
                    <SummaryHorizontalBarChart rows={duplicateChartRows} valueKey="value" name="Duplicate records" color={SUMMARY_COLORS.red} />
                    <SummaryTopRecordsTable rows={duplicateInvoiceRecords} emptyLabel="No duplicate invoice records available" />
                  </SummarySection>
                </Grid>
              )}
              {shouldRenderSummaryTab("priceHistory") && (
                <Grid item xs={12}>
                  <SummarySection title="Item Price History & Price Movement" subtitle="From core_reports.11_item_price_history and item_analytics.price_movement">
                    <SummaryHorizontalBarChart rows={priceHistoryChartRows} valueKey="value" name="Price" formatter={formatSummaryCurrency} color={SUMMARY_COLORS.purple} />
                    <SummaryTopRecordsTable rows={priceHistoryRecords} emptyLabel="No item price history records available" />
                  </SummarySection>
                </Grid>
              )}
              {shouldRenderSummaryTab("apAging") && (
                <Grid item xs={12}>
                  <SummarySection title="Accounts Payable Due & Aging" subtitle={`As of ${apAgingReport.as_of_date || "the latest response date"}`}>
                    <SummaryHorizontalBarChart rows={visibleApAgingChartRows} valueKey="value" name="Aging value" formatter={formatSummaryCurrency} color={SUMMARY_COLORS.red} />
                    <SummaryTopRecordsTable rows={apAgingRecords} emptyLabel="No AP aging records available" />
                    {Object.keys(apAgingReport.bucket_totals || {}).length > 0 && (
                      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        {Object.entries(apAgingReport.bucket_totals).map(([bucket, value]) => (
                          <Grid item xs={12} sm={6} md={3} key={bucket}>
                            <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                              <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{formatSummaryFieldLabel(bucket)}</Typography>
                              <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text }}>{formatSummaryCurrency(value)}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </SummarySection>
                </Grid>
              )}
              {shouldRenderSummaryTab("cashFlow") && (
                <Grid item xs={12}>
                  <SummarySection title="Cash-Flow Projection" subtitle={cashFlowReport.available === false ? cashFlowReport.data_basis || "Cash-flow data is unavailable" : "Projected liabilities by horizon"}>
                    <SummaryHorizontalBarChart rows={visibleCashFlowChartRows} valueKey="value" name="Projected value" formatter={formatSummaryCurrency} color={SUMMARY_COLORS.cyan} />
                    <SummaryTopRecordsTable rows={cashFlowRecords} emptyLabel="No cash-flow projection records available" />
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      {Object.entries(cashFlowReport.horizon_totals || {}).map(([horizon, value]) => (
                        <Grid item xs={12} sm={6} md={3} key={horizon}>
                          <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                            <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>{formatSummaryFieldLabel(horizon)}</Typography>
                            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text }}>{formatSummaryCurrency(value)}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: SUMMARY_COLORS.border, bgcolor: SUMMARY_COLORS.soft }}>
                          <Typography sx={{ ...summaryFont, color: SUMMARY_COLORS.muted }}>Unavailable invoices</Typography>
                          <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 18, color: SUMMARY_COLORS.text }}>{formatSummaryNumber(cashFlowReport.unavailable_invoice_count)}</Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </SummarySection>
                </Grid>
              )}
            </Grid>
          </>
        )}

      </Box>
    </Box>
  );
}

/* ─────────────────────────── StatCard ─────────────────────────── */
function StatCard({ title, value, subtitle, icon, color, progress, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (loading) {
    return (
      <Card elevation={1} sx={{ height: "100%", borderRadius: 3, p: 3, bgcolor: "#f8f9fa" }}>
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={36} />
        {progress !== undefined && (
          <Box mt={2}><Skeleton variant="rounded" height={6} sx={{ borderRadius: 4 }} /></Box>
        )}
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e8edf2",
        background: "#fff",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        },
      }}
    >
      <CardContent sx={{ p: "20px !important" }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                fontFamily: GILROY_MEDIUM, fontWeight: 600,
                fontSize: "14px", color: "#64748b",
                mb: 0.75, lineHeight: 1.4,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontFamily: GILROY_BOLD, fontWeight: 800,
                fontSize: isMobile ? "22px" : "26px", color: color || "#0f172a",
                lineHeight: 1.1, mb: subtitle ? 0.5 : 0,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              ml: 1.5, flexShrink: 0, width: 44, height: 44,
              borderRadius: 2.5, display: "flex", alignItems: "center",
              justifyContent: "center", bgcolor: `${color}18`,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: "1.4rem", color } })}
          </Box>
        </Box>
        {progress !== undefined && (
          <Box mt={2}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
                "& .MuiLinearProgress-bar": {
                  background: `linear-gradient(90deg, ${color}70, ${color})`,
                  borderRadius: 3,
                },
              }}
            />
            <Box display="flex" justifyContent="space-between" mt={0.75}>
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8" }}>Progress</Typography>
              <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", color }}>{progress}%</Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── InvoiceTrendChart (Area style like Image 1) ─────────────────────────── */
const MONTH_ORDER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function InvoiceTrendChart({ data, loading, titleSuffix, variant = "card", exporting = false }) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = exporting ? false : isViewportMobile;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 160 }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", mb: 0.75, color: "#334155" }}>{label}</Typography>
        {payload.map((entry) => (
          <Box key={entry.dataKey} display="flex" alignItems="center" gap={0.75} mb={0.25}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: entry.color }} />
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#475569" }}>
              {entry.name}: <strong>{entry.value}</strong>
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  };

  const content = () => {
    if (loading || !Array.isArray(data) || data.length === 0) {
      return (
        <>
          <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
        </>
      );
    }

    return (
      <>
        <Typography
          sx={{
            fontFamily: GILROY_BOLD, fontWeight: 700,
            fontSize: isMobile ? "15px" : "16px", color: "#1e293b", mb: 0.5,
          }}
        >
          Invoice Processing Trends{titleSuffix ? ` — ${titleSuffix}` : ""}
        </Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", mb: 2 }}>
          Total invoices vs fully matched
        </Typography>

        {/* Legend — dot style like Image 1 */}
        <Box display="flex" gap={3} mb={2} flexWrap="wrap">
          {[
            { color: "#3b82f6", label: "Monthly" },
            { color: "#22c55e", label: "Matched" },
          ].map(({ color, label }) => (
            <Box key={label} display="flex" alignItems="center" gap={0.75}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#64748b" }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, minHeight: 220 }}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="twmGradTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.03} />
                </linearGradient>
                <linearGradient id="twmGradMatched" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#94a3b8", fontFamily: GILROY_MEDIUM, fontSize: 14 }}
                tickMargin={8}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#94a3b8", fontFamily: GILROY_MEDIUM, fontSize: 14 }}
                tickMargin={4}
                width={28}
              />
              <RechartsTooltip content={<CustomTooltip />} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="invoices"
                name="Total Invoices"
                stroke="#3b82f6"
                strokeWidth={2.5}
                fill="url(#twmGradTotal)"
                dot={{ fill: "#3b82f6", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
              />
              <Area
                type="monotone"
                dataKey="matched"
                name="Fully Matched"
                stroke="#22c55e"
                strokeWidth={2.5}
                fill="url(#twmGradMatched)"
                dot={{ fill: "#22c55e", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </>
    );
  };

  const wrapperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    height: "100%",
    minHeight: 340,
    background: "#fff",
    border: "1px solid #e8edf2",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden",
  };

  if (variant === "embedded") return <Box sx={wrapperSx}>{content()}</Box>;
  return <Paper elevation={0} sx={wrapperSx}>{content()}</Paper>;
}

/* ─────────────────────────── MismatchPieChart (Image 2 style donut) ─────────────────────────── */
function MismatchPieChart({ data, loading, titleSuffix, variant = "card", exporting = false }) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = exporting ? false : isViewportMobile;
  const [chartData, setChartData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const PIE_COLORS = ["#3b82f6", "#22c55e", "#06b6d4", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#f97316"];

  useEffect(() => {
    if (!Array.isArray(data)) { setChartData([]); return; }
    const formatted = data
      .map((item) => {
        if (Array.isArray(item)) return { name: String(item[0] || "").trim() || "Unknown", value: Number(item[1]) || 0 };
        if (item && typeof item === "object") return { name: String(item.name || "").trim() || "Unknown", value: Number(item.value) || 0 };
        return null;
      })
      .filter(Boolean)
      .filter((x) => x.value > 0);
    setChartData(formatted);
  }, [data]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const total = (chartData || []).reduce((s, x) => s + x.value, 0);
    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
    return (
      <Paper elevation={4} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${item.payload.fill}40`, minWidth: 160 }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", mb: 0.5 }}>{item.name}</Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#475569" }}>
          {item.value.toLocaleString()} — {pct}%
        </Typography>
      </Paper>
    );
  };

  const content = () => {
    if (loading || !chartData) {
      return (
        <>
          <Skeleton variant="text" width="60%" height={24} sx={{ mx: "auto", mb: 2 }} />
          <Skeleton variant="circular" width={160} height={160} sx={{ mx: "auto", mb: 2 }} />
          <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" width={80} height={20} sx={{ borderRadius: 4 }} />)}
          </Box>
        </>
      );
    }

    const total = chartData.reduce((s, x) => s + x.value, 0);

    return (
      <>
        <Typography
          sx={{
            fontFamily: GILROY_BOLD, fontWeight: 700,
            fontSize: isMobile ? "15px" : "16px", color: "#1e293b",
            mb: 0.5, textAlign: "center",
          }}
        >
          Mismatch Distribution{titleSuffix ? ` — ${titleSuffix}` : ""}
        </Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", mb: 1, textAlign: "center" }}>
          By reason type
        </Typography>

        {/* Donut chart — thick ring like Image 2 */}
        <Box sx={{ position: "relative", width: "100%", height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={82}
                innerRadius={54}
                dataKey="value"
                paddingAngle={3}
                cornerRadius={6}
                startAngle={90}
                endAngle={-270}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                    stroke="#fff"
                    strokeWidth={2}
                    opacity={activeIndex === null || activeIndex === index ? 1 : 0.65}
                  />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 9999 }} />
            </RechartsPieChart>
          </ResponsiveContainer>

          {/* Center total — like Image 2 */}
          <Box
            sx={{
              position: "absolute", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none",
            }}
          >
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8" }}>Total</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#1e293b", lineHeight: 1.1 }}>
              {total.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Bottom dot legend — Image 2 style */}
        <Box
          sx={{
            mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9",
            display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 16px",
          }}
        >
          {chartData.map((entry, index) => {
            const pct = total > 0 ? ((entry.value / total) * 100).toFixed(0) : 0;
            return (
              <Box
                key={index}
                display="flex" alignItems="center" gap={0.75}
                sx={{
                  cursor: "pointer", borderRadius: 3, px: 0.75, py: 0.25,
                  bgcolor: activeIndex === index ? "#f8fafc" : "transparent",
                  transition: "background 0.15s",
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: PIE_COLORS[index % PIE_COLORS.length], flexShrink: 0 }} />
                <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#475569", whiteSpace: "nowrap" }}>
                  {entry.name} <span style={{ color: "#94a3b8" }}>({pct}%)</span>
                </Typography>
              </Box>
            );
          })}
        </Box>
      </>
    );
  };

  const wrapperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    height: "100%",
    background: "#fff",
    border: "1px solid #e8edf2",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden",
  };

  if (variant === "embedded") return <Box sx={wrapperSx}>{content()}</Box>;
  return <Paper elevation={0} sx={wrapperSx}>{content()}</Paper>;
}

/* ─────────────────────────── MismatchReasonsTable ─────────────────────────── */
function MismatchReasonsTable({ data, loading }) {
  const theme = useTheme();

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e8edf2", height: "100%" }}>
        <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
        {[1, 2, 3].map((i) => (
          <Box key={i} display="flex" justifyContent="space-between" mb={2}>
            <Skeleton variant="rounded" width="55%" height={24} />
            <Skeleton variant="rounded" width="15%" height={24} />
            <Skeleton variant="rounded" width="15%" height={24} />
          </Box>
        ))}
      </Paper>
    );
  }

  const totalMismatches = data.reduce((s, [, c]) => s + c, 0);

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e8edf2", height: "100%" }}>
      <Box display="flex" alignItems="center" mb={2.5}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 700, fontSize: "16px", color: "#1e293b", flex: 1 }}>
          Top Mismatch Reasons
        </Typography>
        <Tooltip
          title={`Based on ${totalMismatches} total mismatches`}
          slotProps={{ tooltip: { sx: { fontFamily: GILROY_MEDIUM, fontSize: "14px" } } }}
        >
          <IconButton size="small" sx={{ color: "#94a3b8" }}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ "& th": { py: 1.25, px: 1.5, borderBottom: "2px solid #f1f5f9" } }}>
            {["Reason", "Count", "%"].map((h, i) => (
              <TableCell
                key={i}
                align={i === 0 ? "left" : "right"}
                sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#94a3b8", fontFamily: GILROY_MEDIUM, fontSize: "14px", fontStyle: "italic" }}>
                No mismatches found
              </TableCell>
            </TableRow>
          ) : (
            data.map(([reason, count], idx) => {
              const pct = totalMismatches > 0 ? Math.round((count / totalMismatches) * 100) : 0;
              const barColors = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];
              const barColor = barColors[idx % barColors.length];
              return (
                <TableRow
                  key={idx}
                  sx={{
                    "&:hover": { bgcolor: "#f8fafc" },
                    "& td": { py: 1.5, px: 1.5, borderBottom: "1px solid #f8fafc" },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#334155", mb: 0.5 }}>
                        {reason}
                      </Typography>
                      {/* Mini progress bar */}
                      <Box sx={{ height: 3, borderRadius: 2, bgcolor: "#f1f5f9", overflow: "hidden", maxWidth: 180 }}>
                        <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: barColor, borderRadius: 2, transition: "width 0.4s" }} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", color: "#334155" }}>{count}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${pct}%`}
                      size="small"
                      sx={{
                        fontFamily: GILROY_BOLD, fontSize: "14px", fontWeight: 700,
                        height: 22, bgcolor: `${barColor}15`, color: barColor,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {totalMismatches > 0 && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2 }}>
          <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", fontStyle: "italic" }}>
            Percentages are relative to {totalMismatches} total mismatches across all records.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

/* ─────────────────────────── RecordsSnapshot ─────────────────────────── */
function RecordsSnapshot({ data, per, loading, onViewDetails, exporting = false }) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = exporting ? false : isViewportMobile;

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e8edf2" }}>
        <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} display="flex" justifyContent="space-between" mb={2}>
            {[1, 2, 3, 4, 5].map((j) => <Skeleton key={j} variant="rounded" width="15%" height={28} />)}
          </Box>
        ))}
      </Paper>
    );
  }

  const statusChipSx = (status) => {
    const s = String(status || "").toUpperCase();
    const map = {
      APPROVED: { bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" },
      IN_REVIEW: { bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" },
      REJECTED:  { bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" },
    };
    return map[s] || { bgcolor: "#fff7ed", color: "#d97706", border: "1px solid #fde68a" };
  };

  const classChipSx = (cls) => {
    if (cls === "Fully Matched") return { bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" };
    if (cls === "Partially Matched") return { bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" };
    return { bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" };
  };

  const sorted = [...data]
    .map((item, i) => ({ rec: item, p: per[i] }))
    .sort((a, b) => {
      const numA = parseInt((a.rec.match_code || "").split("/").pop()) || 0;
      const numB = parseInt((b.rec.match_code || "").split("/").pop()) || 0;
      return numB - numA;
    })
    .slice(0, 5);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8edf2", overflow: "hidden" }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, borderBottom: "1px solid #f1f5f9" }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
          Recent Invoices
        </Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", mt: 0.25 }}>
          Latest 5 records
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "#f8fafc",
                "& th": {
                  py: 1.5, px: { xs: 1.5, sm: 2 },
                  borderBottom: "1px solid #e8edf2",
                  fontFamily: GILROY_BOLD, fontSize: "14px",
                  fontWeight: 700, color: "#64748b", whiteSpace: "nowrap",
                },
              }}
            >
              {["Match Code", "Status", "Classification", "Amount", "Mismatches", ""].map((h, i) => (
                <TableCell key={i}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#94a3b8", fontFamily: GILROY_MEDIUM, fontStyle: "italic", fontSize: "14px" }}>
                  No records available
                </TableCell>
              </TableRow>
            ) : (
              sorted.map(({ rec, p }) => {
                const rowSummary = p || {
                  classification: "With Exceptions",
                  invAmount: 0,
                  mismatches: [],
                };

                return (
                <TableRow
                  key={rec.token || rec.match_code}
                  hover
                  sx={{
                    "&:hover": { bgcolor: "#f8fafc" },
                    "& td": {
                      py: 1.75, px: { xs: 1.5, sm: 2 },
                      borderBottom: "1px solid #f8fafc",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", color: "#334155", fontWeight: 700 }}>
                      {rec.match_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getRecordStatusDisplay(rec.status)}
                      size="small"
                      sx={{
                        fontFamily: GILROY_MEDIUM, fontSize: "14px", fontWeight: 600,
                        height: 26, borderRadius: 1.5, ...statusChipSx(rec.status),
                        "& .MuiChip-label": { px: 1.25 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rowSummary.classification}
                      size="small"
                      sx={{
                        fontFamily: GILROY_MEDIUM, fontSize: "14px", fontWeight: 600,
                        height: 26, borderRadius: 1.5, ...classChipSx(rowSummary.classification),
                        "& .MuiChip-label": { px: 1.25 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", color: "#334155" }}>
                      {toINR(rowSummary.invAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: GILROY_MEDIUM, fontSize: "14px",
                        color: rowSummary.mismatches.length ? "#ef4444" : "#94a3b8",
                      }}
                    >
                      {rowSummary.mismatches.length > 0
                        ? rowSummary.mismatches[0].replace(/:.+$/, "").trim()
                        : "—"}
                      {rowSummary.mismatches.length > 1 ? ` +${rowSummary.mismatches.length - 1}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View details" slotProps={{ tooltip: { sx: { fontFamily: GILROY_MEDIUM, fontSize: "14px" } } }}>
                      <IconButton
                        size="small"
                        onClick={() => onViewDetails(rec, rowSummary)}
                        sx={{
                          width: 32, height: 32, borderRadius: 1.5,
                          bgcolor: "#f1f5f9", color: "#64748b",
                          "&:hover": { bgcolor: theme.palette.primary.main, color: "#fff" },
                          transition: "all 0.15s",
                        }}
                      >
                        <Visibility sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/* ─────────────────────────── DetailDialog ─────────────────────────── */
function DetailDialog({ open, onClose, record, reconciliation }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  if (!record || !reconciliation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: isMobile ? 0.5 : 1 } }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #f1f5f9", py: isMobile ? 2 : 2.5, px: isMobile ? 2 : 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: isMobile ? "16px" : "18px", color: "#1e293b" }}>
            {record.match_code}
          </Typography>
          <Chip
            label={reconciliation.classification}
            size="small"
            sx={{
              fontFamily: GILROY_MEDIUM, fontSize: "14px", fontWeight: 600, height: 28,
              bgcolor: reconciliation.classification === "Fully Matched" ? "#f0fdf4" :
                       reconciliation.classification === "Partially Matched" ? "#fff7ed" : "#fef2f2",
              color: reconciliation.classification === "Fully Matched" ? "#16a34a" :
                     reconciliation.classification === "Partially Matched" ? "#c2410c" : "#dc2626",
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ py: 3, px: isMobile ? 2 : 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#64748b", mb: 0.5 }}>Status</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "16px", color: "#1e293b" }}>{record.status}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#64748b", mb: 0.5 }}>Invoice Amount</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#3b82f6" }}>{toINR(reconciliation.invAmount)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#64748b", mb: 1 }}>Mismatches</Typography>
            {reconciliation.mismatches.length > 0 ? (
              <Box sx={{ border: "1px solid #fecaca", borderRadius: 2, bgcolor: "#fff5f5", p: 2 }}>
                {reconciliation.mismatches.map((m, i) => (
                  <Box key={i} display="flex" alignItems="flex-start" gap={1} mb={i < reconciliation.mismatches.length - 1 ? 1 : 0}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#ef4444", mt: "6px", flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#334155", lineHeight: 1.6 }}>{m}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", fontStyle: "italic" }}>
                No mismatches found
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: isMobile ? 2 : 3, py: 2, borderTop: "1px solid #f1f5f9" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ borderRadius: 2, fontFamily: GILROY_BOLD, fontSize: "14px", textTransform: "none", px: 4, py: 1 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─────────────────────────── Skeleton / Error ─────────────────────────── */
function LoadingSkeleton() {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="35%" height={36} sx={{ mb: 3 }} />
      <Grid container spacing={2.5}>
        {[...Array(8)].map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function ErrorDisplay({ message, onRetry }) {
  return (
    <Box sx={{ p: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <ErrorOutline sx={{ fontSize: 56, color: "#ef4444", mb: 2, opacity: 0.8 }} />
      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#dc2626", mb: 1 }}>Something went wrong</Typography>
      <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#64748b", mb: 3, maxWidth: 480 }}>{message}</Typography>
      <Button variant="contained" onClick={onRetry} startIcon={<Refresh />} sx={{ borderRadius: 2, fontFamily: GILROY_BOLD, textTransform: "none" }}>
        Try Again
      </Button>
    </Box>
  );
}

/* ─────────────────────────── Year / Month helpers ─────────────────────────── */
function getAvailableYears(per = [], fallbackYear = new Date().getFullYear()) {
  const years = new Set();
  per.forEach((p) => {
    if (!p?.createdAt) return;
    const d = new Date(p.createdAt);
    if (!Number.isNaN(d.getTime())) years.add(d.getFullYear());
  });
  const sorted = [...years].sort((a, b) => a - b);
  return sorted.length ? sorted : [fallbackYear];
}

function filterPerByYearMonth(per = [], year, month) {
  if (!Array.isArray(per) || !year) return [];
  const y = Number(year);
  const monthIndex = month === "all" ? null : Number(month);
  return per.filter((p) => {
    if (!p?.createdAt) return false;
    const d = new Date(p.createdAt);
    if (Number.isNaN(d.getTime())) return false;
    if (d.getFullYear() !== y) return false;
    return monthIndex === null || d.getMonth() === monthIndex;
  });
}

function buildInvoiceTrendSeries(per = [], year, month, now = new Date()) {
  if (!year) return [];
  const y = Number(year);
  const isAllMonths = month === "all";
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();

  if (!isAllMonths) {
    const monthIndex = Number(month);
    if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return [];
    const daysInMonth = new Date(y, monthIndex + 1, 0).getDate();
    const limitDays = y === currentYear && monthIndex === currentMonthIndex
      ? Math.min(daysInMonth, now.getDate()) : daysInMonth;
    const byDay = new Map();
    per.forEach((p) => {
      if (!p?.createdAt) return;
      const d = new Date(p.createdAt);
      if (Number.isNaN(d.getTime()) || d.getFullYear() !== y || d.getMonth() !== monthIndex) return;
      const key = String(d.getDate()).padStart(2, "0");
      const curr = byDay.get(key) || { name: key, invoices: 0, matched: 0 };
      const invoiceCount = Number(p.invoices ?? 1);
      curr.invoices += Number.isFinite(invoiceCount) ? invoiceCount : 1;
      if (p.matched != null) {
        const matchedCount = Number(p.matched);
        curr.matched += Number.isFinite(matchedCount) ? matchedCount : 0;
      } else if (p.classification === "Fully Matched") curr.matched += 1;
      byDay.set(key, curr);
    });
    return Array.from({ length: limitDays }, (_, i) => {
      const key = String(i + 1).padStart(2, "0");
      return byDay.get(key) || { name: key, invoices: 0, matched: 0 };
    });
  }

  let latestCapturedMonth = -1;
  if (y === currentYear) {
    per.forEach((p) => {
      if (!p?.createdAt) return;
      const d = new Date(p.createdAt);
      if (!Number.isNaN(d.getTime()) && d.getFullYear() === y)
        latestCapturedMonth = Math.max(latestCapturedMonth, d.getMonth());
    });
  }
  const maxMonthsToShow = y === currentYear
    ? Math.min(currentMonthIndex, latestCapturedMonth >= 0 ? latestCapturedMonth : currentMonthIndex) + 1
    : 12;

  const byMonth = new Map();
  per.forEach((p) => {
    if (!p?.createdAt) return;
    const d = new Date(p.createdAt);
    if (Number.isNaN(d.getTime()) || d.getFullYear() !== y) return;
    const monthName = MONTH_ORDER[d.getMonth()];
    const curr = byMonth.get(monthName) || { name: monthName, invoices: 0, matched: 0 };
    const invoiceCount = Number(p.invoices ?? 1);
    curr.invoices += Number.isFinite(invoiceCount) ? invoiceCount : 1;
    if (p.matched != null) {
      const matchedCount = Number(p.matched);
      curr.matched += Number.isFinite(matchedCount) ? matchedCount : 0;
    } else if (p.classification === "Fully Matched") curr.matched += 1;
    byMonth.set(monthName, curr);
  });

  return MONTH_ORDER.slice(0, maxMonthsToShow).map(
    (m) => byMonth.get(m) || { name: m, invoices: 0, matched: 0 }
  );
}

/* ─────────────────────────── Main Dashboard ─────────────────────────── */
export default function TWMOverviewDashboard({
  data = [],
  summaryData = null,
  loading = false,
  onExportDashboard,
  onRefresh,
}) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLoading = loading && !summaryData;
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const isMobile = exporting ? false : isViewportMobile;
  const dashboardRef = useRef(null);
  const now = useMemo(() => new Date(), []);

  const [detailDialog, setDetailDialog] = useState({ open: false, record: null, reconciliation: null });

  const { totals, per } = useMemo(() => {
    try {
      return summaryData?.overview
        ? computeSummaryKPIs(summaryData)
        : summaryData?.dashboard_metrics
        ? computeSummaryKPIs(summaryData)
        : computeKPIs(data);
    }
    catch (err) { setError(err.message); return { totals: {}, per: [] }; }
  }, [data, summaryData]);

  const recordPer = useMemo(() => {
    if (summaryData?.overview) return [];
    try {
      return computeKPIs(data).per;
    } catch {
      return [];
    }
  }, [data, summaryData?.overview]);

  const availableYears = useMemo(() => getAvailableYears(per, now.getFullYear()), [per, now]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    if (!availableYears.length) return;
    const latest = availableYears[availableYears.length - 1];
    if (!selectedYear || !availableYears.includes(selectedYear)) setSelectedYear(latest);
  }, [availableYears, selectedYear]);

  const maxMonthInDataForSelectedYear = useMemo(() => {
    if (!selectedYear) return -1;
    const y = Number(selectedYear);
    let max = -1;
    per.forEach((p) => {
      if (!p?.createdAt) return;
      const d = new Date(p.createdAt);
      if (!Number.isNaN(d.getTime()) && d.getFullYear() === y) max = Math.max(max, d.getMonth());
    });
    return max;
  }, [per, selectedYear]);

  const maxMonthIndexForYear = useMemo(() => {
    if (!selectedYear) return 11;
    const yearNum = Number(selectedYear);
    if (yearNum !== now.getFullYear()) return 11;
    const latestCaptured = maxMonthInDataForSelectedYear >= 0 ? maxMonthInDataForSelectedYear : now.getMonth();
    return Math.min(now.getMonth(), latestCaptured);
  }, [selectedYear, now, maxMonthInDataForSelectedYear]);

  useEffect(() => {
    if (selectedMonth === "all") return;
    const idx = Number(selectedMonth);
    if (!Number.isFinite(idx) || idx < 0 || idx > maxMonthIndexForYear) setSelectedMonth("all");
  }, [selectedMonth, maxMonthIndexForYear]);

  const monthOptions = useMemo(
    () => MONTH_ORDER.slice(0, maxMonthIndexForYear + 1).map((label, idx) => ({ value: idx, label })),
    [maxMonthIndexForYear]
  );

  const filteredPerForCharts = useMemo(
    () => filterPerByYearMonth(per, selectedYear, selectedMonth),
    [per, selectedYear, selectedMonth]
  );

  const trendSeries = useMemo(
    () => buildInvoiceTrendSeries(per, selectedYear, selectedMonth, now),
    [per, selectedYear, selectedMonth, now]
  );

  const trendTitleSuffix = useMemo(() => {
    if (!selectedYear) return "";
    if (selectedMonth === "all") return String(selectedYear);
    return `${MONTH_ORDER[Number(selectedMonth)] || ""} ${selectedYear}`;
  }, [selectedYear, selectedMonth]);

  const mismatchReasonEntries = useMemo(() => {
    const summaryReasons =
      summaryData?.dashboard_metrics?.top_3_mismatch_reasons ||
      summaryData?.analytics_kpis?.top_mismatch_reasons;

    if (Array.isArray(summaryReasons) && summaryReasons.length > 0) {
      return summaryReasons
        .map((item) => [item?.reason || "Unknown", Number(item?.count || 0)])
        .sort((a, b) => b[1] - a[1]);
    }

    const reasonCount = new Map();
    filteredPerForCharts.forEach((p) => {
      (p?.reasonBuckets || []).forEach((r) => {
        if (!r) return;
        reasonCount.set(r, (reasonCount.get(r) || 0) + 1);
      });
    });
    return [...reasonCount.entries()].sort((a, b) => b[1] - a[1]);
  }, [filteredPerForCharts, summaryData]);

  const mismatchDonutData = useMemo(() => {
    const MAX_SLICES = 6;
    const top = mismatchReasonEntries.slice(0, MAX_SLICES);
    const rest = mismatchReasonEntries.slice(MAX_SLICES);
    const otherCount = rest.reduce((s, [, c]) => s + c, 0);
    return otherCount > 0 ? [...top, ["Other", otherCount]] : top;
  }, [mismatchReasonEntries]);

  const mismatchTableData = useMemo(() => mismatchReasonEntries.slice(0, 6), [mismatchReasonEntries]);

  useEffect(() => {
    if (!loading && (data.length || summaryData?.overview || summaryData?.dashboard_metrics)) setError(null);
  }, [data, loading, summaryData]);

  const handleRefresh = () => onRefresh?.();

  /* PDF Export — unchanged from original */
  const exportDashboardAsPDF = async () => {
    const exportRoot = dashboardRef.current;
    if (!exportRoot) return;
    const waitForDashboardCharts = async (root, minimumWait = 2800, timeout = 10000) => {
      const startedAt = Date.now();
      let stableChecks = 0;
      let previousSignature = "";

      while (Date.now() - startedAt < timeout) {
        const chartContainers = Array.from(
          root.querySelectorAll(".recharts-responsive-container"),
        );
        const chartsReady = chartContainers.every(
          (container) =>
            container.offsetWidth > 0 &&
            container.offsetHeight > 0 &&
            container.querySelector(".recharts-surface"),
        );
        const chartSignature = chartContainers
          .map((container) =>
            [
              container.offsetWidth,
              container.offsetHeight,
              container.querySelectorAll(".recharts-surface").length,
            ].join("x"),
          )
          .join("|");

        if (chartsReady && chartContainers.length > 0 && chartSignature === previousSignature) {
          stableChecks += 1;
        } else {
          stableChecks = 0;
        }
        previousSignature = chartSignature;

        if (
          chartsReady &&
          stableChecks >= 3 &&
          Date.now() - startedAt >= minimumWait
        ) {
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    };
    const renderOptions = {
      scale: 2, useCORS: true, allowTaint: false, backgroundColor: "#ffffff",
      logging: false, scrollX: 0, scrollY: 0,
      ignoreElements: (el) => !!(el?.hasAttribute?.("data-export-ignore") || el?.getAttribute?.("data-export-ignore") === "true"),
      onclone: (clonedDoc) => {
        const clonedRoot = clonedDoc.querySelector('[data-dashboard-export-root="true"]');
        if (clonedRoot) {
          clonedRoot.style.width = `${PDF_EXPORT_WIDTH}px`;
          clonedRoot.style.maxWidth = "none";
          clonedRoot.style.overflow = "visible";
        }
        clonedDoc.querySelectorAll(".MuiTableContainer-root").forEach((node) => {
          node.style.maxHeight = "none";
          node.style.height = "auto";
          node.style.overflow = "visible";
        });
        clonedDoc.querySelectorAll("[data-export-section]").forEach((node) => {
          node.style.maxHeight = "none";
          node.style.overflow = "visible";
        });
        clonedDoc.querySelectorAll(".recharts-responsive-container").forEach((node) => {
          node.style.width = "100%";
          node.style.height = node.style.height || "300px";
          node.style.minHeight = node.style.height;
          node.style.overflow = "visible";
        });
        clonedDoc.querySelectorAll(".recharts-wrapper, .recharts-surface").forEach((node) => {
          node.style.overflow = "visible";
          node.style.animation = "none";
          node.style.transition = "none";
        });
        clonedDoc.documentElement.style.width = `${PDF_EXPORT_WIDTH}px`;
        clonedDoc.body.style.width = `${PDF_EXPORT_WIDTH}px`;
      },
    };
    const pdf = new jsPDF("p", "mm", "a4");
    const margin = 8;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;
    const addCanvasToPdf = (canvas) => {
      if (!canvas?.width || !canvas?.height) return false;
      const pageHeightPx = Math.max(1, Math.floor((usableHeight * canvas.width) / usableWidth));
      const sliceCanvas = document.createElement("canvas");
      const sliceCtx = sliceCanvas.getContext("2d");
      if (!sliceCtx) return false;
      let sourceY = 0, hasRendered = false;
      while (sourceY < canvas.height) {
        const sliceH = Math.min(pageHeightPx, canvas.height - sourceY);
        sliceCanvas.width = canvas.width; sliceCanvas.height = sliceH;
        sliceCtx.fillStyle = "#fff";
        sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        sliceCtx.drawImage(canvas, 0, sourceY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        let imgData = "";
        try { imgData = sliceCanvas.toDataURL("image/jpeg", 0.95); } catch { return hasRendered; }
        if (typeof imgData !== "string" || !imgData.startsWith("data:image/")) return hasRendered;
        const imgH = (sliceH * usableWidth) / canvas.width;
        if (!Number.isFinite(imgH) || imgH <= 0) return hasRendered;
        if (hasRendered) pdf.addPage();
        pdf.addImage(imgData, "JPEG", margin, margin, usableWidth, imgH);
        hasRendered = true; sourceY += sliceH;
      }
      return hasRendered;
    };
    try {
      setExporting(true);
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      await waitForDashboardCharts(exportRoot);
      try { if (document.fonts?.ready) await document.fonts.ready; } catch (_) {}
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const rw = Math.max(PDF_EXPORT_WIDTH, Math.ceil(exportRoot.scrollWidth || exportRoot.offsetWidth || 0));
      const rh = Math.max(1, Math.ceil(exportRoot.scrollHeight || exportRoot.offsetHeight || 0));
      const canvas = await html2canvas(exportRoot, {
        ...renderOptions,
        width: rw,
        height: rh,
        windowWidth: Math.max(rw, PDF_EXPORT_WIDTH),
        windowHeight: Math.max(rh, PDF_EXPORT_MIN_HEIGHT),
      });
      addCanvasToPdf(canvas);
      pdf.save("Account-Payable-Automation-Dashboard.pdf");
    } catch (e) { console.error("Dashboard PDF export failed:", e); }
    finally { setExporting(false); }
  };

  const handleViewDetails = (record, reconciliation) => setDetailDialog({ open: true, record, reconciliation });
  const handleCloseDetails = () => setDetailDialog({ open: false, record: null, reconciliation: null });

  if (isLoading && !data.length && !summaryData?.overview) return <LoadingSkeleton />;
  if (error) return <ErrorDisplay message={error} onRetry={() => window.location.reload()} />;

  if (summaryData?.overview) {
    return (
      <SummaryAnalyticsDashboard
        summaryData={summaryData}
        loading={isLoading}
        exporting={exporting}
        dashboardRef={dashboardRef}
        exportDashboardAsPDF={exportDashboardAsPDF}
        onRefresh={handleRefresh}
        isMobile={isMobile}
      />
    );
  }

  /* ─── Compact alert card ─── */
  const AlertCard = ({ title, count, value, icon: Icon, accent, detail }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5, borderRadius: 3, border: "1px solid #e8edf2", height: "100%",
        position: "relative", overflow: "hidden",
        "&:before": {
          content: '""', position: "absolute", top: 0, left: 0, right: 0,
          height: 3, background: accent,
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "15px", color: "#1e293b", lineHeight: 1.3, maxWidth: "70%" }}>
          {title}
        </Typography>
        <Chip
          label={count}
          size="small"
          sx={{
            fontFamily: GILROY_BOLD, fontSize: "14px", fontWeight: 700,
            height: 26, bgcolor: `${accent.split(",")[0].replace("linear-gradient(90deg, ", "")}20`,
            color: accent.split(",")[0].replace("linear-gradient(90deg, ", ""),
            "& .MuiChip-label": { px: 1.25 },
          }}
        />
      </Box>
      <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#64748b", mb: value ? 1.5 : 0 }}>{detail}</Typography>
      {value && (
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#1e293b" }}>{value}</Typography>
      )}
    </Paper>
  );

  return (
    <Box
      sx={{
        width: "100%",          // ← key fix: fill parent width, not 100vw
        minWidth: 0,            // ← allow shrinking with sidebar
        bgcolor: "#f4f6f8",
        pt: 3,
      }}
    >
      <Box
        component="main"
        ref={dashboardRef}
        data-dashboard-export-root="true"
        sx={{
          width: "100%",
          minWidth: 0,
          px: { xs: 2, sm: 3 },
          pb: 4,
          ...(exporting ? { width: `${PDF_EXPORT_WIDTH}px`, minWidth: `${PDF_EXPORT_WIDTH}px`, overflow: "visible" } : {}),
        }}
      >
        {/* ── Header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          mb={3.5}
          data-export-section
        >
          <Box>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 800, fontSize: isMobile ? "20px" : "24px", color: "#1e293b" }}>
              Account Payable Automation Dashboard
            </Typography>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", mt: 0.5 }}>
              Monitor and analyze invoice matching performance
            </Typography>
          </Box>
          <Box display="flex" gap={1.5} mt={isMobile ? 0 : 0} data-export-ignore>
            <Button
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: "18px !important" }} />}
              onClick={handleRefresh}
              size="medium"
              sx={{
                borderRadius: 2, fontFamily: GILROY_MEDIUM, fontSize: "14px",
                textTransform: "none", borderColor: "#e2e8f0", color: "#475569",
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download sx={{ fontSize: "18px !important" }} />}
              onClick={exportDashboardAsPDF}
              size="medium"
              disabled={!data.length || exporting}
              sx={{
                borderRadius: 2, fontFamily: GILROY_BOLD, fontSize: "14px",
                textTransform: "none", boxShadow: "none",
                "&:hover": { boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
              }}
            >
              {exporting ? "Exporting…" : "Export PDF"}
            </Button>
          </Box>
        </Box>

        {/* ── KPI Row 1 ── */}
        <Grid container spacing={2} mb={2.5} data-export-section>
          {[
            { title: "Total Invoices Processed", value: totals.total, icon: <TrendingUp />, color: "#3b82f6" },
            { title: "Fully Matched", value: totals.fully, subtitle: `${Math.round(totals.total ? (totals.fully / totals.total) * 100 : 0)}% of total`, icon: <CheckCircle />, color: "#22c55e" },
            { title: "Partially Matched", value: totals.partial, icon: <Autorenew />, color: "#f59e0b" },
            { title: "With Exceptions", value: totals.withExceptions, icon: <Warning />, color: "#ef4444" },
          ].map((card, i) => (
            <Grid item xs={6} sm={6} md={3} key={i} sx={{ minWidth: 0 }}>
              <StatCard {...card} loading={isLoading} />
            </Grid>
          ))}
        </Grid>

        {/* ── KPI Row 2 ── */}
        <Grid container spacing={2} mb={2.5} data-export-section>
          {[
            { title: "Ready for Payment", value: totals.approved, icon: <Payment />, color: "#06b6d4" },
            { title: "Avg Matching Time", value: totals.avgMatchingTime != null ? `${totals.avgMatchingTime} min` : "N/A", icon: <Schedule />, color: "#8b5cf6" },
            { title: "Automation Coverage", value: `${totals.automationCoverage}%`, icon: <AssignmentTurnedIn />, color: "#10b981", progress: totals.automationCoverage },
            { title: "Total Exception Value", value: toINR(totals.totalExceptionValue), icon: <ErrorOutline />, color: "#f97316" },
          ].map((card, i) => (
            <Grid item xs={6} sm={6} md={3} key={i} sx={{ minWidth: 0 }}>
              <StatCard {...card} loading={isLoading} />
            </Grid>
          ))}
        </Grid>

        {/* ── KPI Row 3 ── */}
        <Grid container spacing={2} mb={3} data-export-section>
          {[
            { title: "Manual Effort Saved", value: `${totals.manualEffortSaved} FTEs/mo`, icon: <Person />, color: "#6366f1" },
            { title: "Aging > 7 Days", value: totals.agingCount, icon: <AccessTime />, color: "#dc2626" },
            { title: "SLA Breach Risk", value: totals.delayedCount, subtitle: `${toINR(totals.delayedValue)} value`, icon: <PendingActions />, color: "#c2410c" },
            {
              title: "Exception Resolution Rate",
              value: totals.withExceptions > 0
                ? `${Math.round(((totals.withExceptions - totals.agingCount) / totals.withExceptions) * 100)}%`
                : "100%",
              subtitle: "Resolved within 7 days",
              icon: <CheckCircle />,
              color: "#16a34a",
            },
          ].map((card, i) => (
            <Grid item xs={6} sm={6} md={3} key={i} sx={{ minWidth: 0 }}>
              <StatCard {...card} loading={isLoading} />
            </Grid>
          ))}
        </Grid>

        {/* ── Trends & Mismatch Charts ── */}
        <Paper
          data-export-section
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3,
            border: "1px solid #e8edf2", bgcolor: "#fff",
          }}
        >
          {/* Filter row */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            mb={2.5}
          >
            <Box>
              <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
                Trends & Mismatch Analysis
              </Typography>
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", mt: 0.25 }}>
                Filter insights by year and month
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" data-export-ignore="true">
              <TextField
                select
                label="Year"
                size="small"
                value={selectedYear ?? ""}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setSelectedYear(Number.isFinite(y) ? y : null);
                  setSelectedMonth("all");
                }}
                sx={{
                  minWidth: 120,
                  "& .MuiOutlinedInput-root": { borderRadius: 2, fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                  "& .MuiInputLabel-root": { fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                }}
              >
                {availableYears.map((y) => (
                  <MenuItem key={y} value={y} sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px" }}>{y}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Month"
                size="small"
                value={selectedMonth}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "all") { setSelectedMonth("all"); return; }
                  const idx = Number(v);
                  setSelectedMonth(Number.isFinite(idx) ? idx : "all");
                }}
                sx={{
                  minWidth: 140,
                  "& .MuiOutlinedInput-root": { borderRadius: 2, fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                  "& .MuiInputLabel-root": { fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                }}
              >
                <MenuItem value="all" sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px" }}>All Months</MenuItem>
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value} sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px" }}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>

          {/* Charts — side by side, collapse to stacked on narrow screens */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
              <InvoiceTrendChart
                data={trendSeries}
                loading={isLoading}
                titleSuffix={trendTitleSuffix}
                variant="embedded"
                exporting={exporting}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
              <MismatchPieChart
                data={mismatchDonutData}
                loading={isLoading}
                titleSuffix={trendTitleSuffix}
                variant="embedded"
                exporting={exporting}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Mismatch Table + Alert Cards ── */}
        <Grid container spacing={2.5} mb={3} data-export-section>
          <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
            <MismatchReasonsTable data={mismatchTableData} loading={isLoading} />
          </Grid>
          <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
            <Stack spacing={2.5} height="100%">
              <AlertCard
                title="Aging Exceptions"
                count={`${totals.agingCount} cases`}
                detail="Cases pending beyond 7 days"
                accent="linear-gradient(90deg, #ef4444, #f87171)"
              />
              <AlertCard
                title="SLA Breach Risk Payments"
                count={`${totals.delayedCount} invoices`}
                detail="Due to unresolved mismatches"
                value={toINR(totals.delayedValue)}
                accent="linear-gradient(90deg, #f59e0b, #fbbf24)"
              />
            </Stack>
          </Grid>
        </Grid>

        {/* ── Recent Invoices ── */}
        <Box data-export-section>
          <RecordsSnapshot
            data={data}
            per={recordPer}
            loading={isLoading}
            onViewDetails={handleViewDetails}
            exporting={exporting}
          />
        </Box>

        {/* ── Detail Dialog ── */}
        <DetailDialog
          open={detailDialog.open}
          onClose={handleCloseDetails}
          record={detailDialog.record}
          reconciliation={detailDialog.reconciliation}
        />
      </Box>
    </Box>
  );
}

